import { BasePdfGenerator } from "../../../../utils/pdf/BasePdfGenerator";
import {
  formatAmount,
  isAutoload,
  sumObjects,
} from "../../../../utils/hooks/dataHandler";
import {
  boldAutoColumn,
  autoTextColor,
  beautyAutoLine,
  autoTextColorNoBreak,
  lineBreakText,
  italicAutoColumn,
} from "../../../../utils/pdf/page";
import { getCaSettingByKey } from "../../../../builders/settings/CaSetting";
import { headerSettings } from "../CaSchedules/utils/tableSettings";
import {
  buildAutoNote,
  buildAutoTable,
  insertTableOfContents,
} from "../../../../utils/pdf/buildAutoTable";
import {
  calculatePrincipalYearTotal,
  calculateTotalAaClaims,
  calculateTotalPrincipalPaid,
  getAaClaimForPrincipalYear,
} from "../HirePurchase/PrincipalPaidTable";
import { calculateTotal } from "../CaSchedules/utils/calculation";
import {
  EXTERNAL_TYPES,
  FIELD_LABELS,
  displayValue,
} from "../FaRegister/rules";

const footerSettings = false;
const FA_REGISTER_PRIOR_YEAR_SOURCE = "fa_register_previous_year";

const isFaRegisterImportedCaRow = (row = {}) =>
  row?.source?.type === FA_REGISTER_PRIOR_YEAR_SOURCE ||
  row?.faRegisterSource ||
  row?.isImportedFromFARegister;

const getCaSchedulePdfYa = (row = {}) => {
  if (isFaRegisterImportedCaRow(row)) return `${row.ya}**`;
  if (row.manualData) return `${row.ya}*`;
  return row.ya;
};

const getCaSchedulePdfRowCaName = (row = {}) => {
  if (["S16 - LIA", "S16 - IBA"].includes(row.caName)) {
    return "S16 - (LIA / IBA)";
  }
  if (row.caName === "S16 - (LIA / IBA)") {
    return row.s16Type || row.caSummary || row.caName;
  }
  return row.caName || "";
};

const appendFaRegisterImportReviewNote = (reviewNotes = "", rows = []) => {
  const importedRows = rows.filter(isFaRegisterImportedCaRow);
  if (!importedRows.length) return reviewNotes;

  const seen = new Set();
  const uniqueRows = importedRows.filter((row) => {
    const key =
      row.rowId ||
      [
        row.ya,
        row.caName,
        row.s16Type,
        row.noOfYearClaims,
        row.noOfYearClaimsBf,
        row.isDeferred,
      ].join("|");
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });

  let nextReviewNotes = reviewNotes || "";
  if (nextReviewNotes.trim() !== "") nextReviewNotes += "\n\n";

  nextReviewNotes += "[FA Register Imported Rows]\n";
  nextReviewNotes += "The row marked YA (**) was imported from FA Register:\n";
  uniqueRows.forEach((row) => {
    const source = row.source?.scheduleNo
      ? ` | Source: Sch ${row.source.scheduleNo}`
      : "";
    nextReviewNotes += `- YA: ${row.ya}, CA Name: ${getCaSchedulePdfRowCaName(
      row
    )}${source}\n`;
  });

  return nextReviewNotes;
};

const getRowCaSummaryKeyFromRow = (row) => {
  let caKey = `${row.caSummary}`;

  if (["S16 - LIA,S16 - IBA"].includes(caKey)) {
    if (row.s16Type) caKey = `${row.s16Type}`;
    else caKey = `${row.caName}`;
  }
  if (caKey === "S16" && row.isHirePurchase) {
    caKey = `${row.caName}`;
  }

  return caKey && caKey !== "" ? caKey : null;
};

const buildFaSummaryCalculatorNotes = (
  reviewNotes,
  costRows,
  accDepreciationRows,
  needRound = true
) => {
  if (!costRows && !accDepreciationRows) return reviewNotes;

  const allRows = [
    ...(costRows || []).map((row) => ({ ...row, type: "Cost" })),
    ...(accDepreciationRows || []).map((row) => ({
      ...row,
      type: "Accumulated Depreciation",
    })),
  ];

  const historyAmountRows = allRows.filter((row) => {
    return (
      (row.openingBalanceHistoryAmount &&
        row.openingBalanceHistoryAmount.length > 1) ||
      (row.additionsHistoryAmount && row.additionsHistoryAmount.length > 1) ||
      (row.disposalHistoryAmount && row.disposalHistoryAmount.length > 1)
    );
  });

  if (historyAmountRows && historyAmountRows.length > 0) {
    if (reviewNotes !== "") reviewNotes += "\n\n";
    reviewNotes += "[Amount Calculation]";

    historyAmountRows.forEach((row) => {
      const categoryName = row.assetCategory || "Unnamed Category";
      const typeLabel = row.type || "";

      // Opening Balance calculation
      if (
        row.openingBalanceHistoryAmount &&
        row.openingBalanceHistoryAmount.length > 1
      ) {
        const amountBreakdown = row.openingBalanceHistoryAmount
          .map((amount) => formatAmount(amount, needRound))
          .join(" + ");
        reviewNotes += `\n${categoryName} (${typeLabel}) - Opening Balance: ${formatAmount(
          row.openingBalance,
          needRound
        )} = ${amountBreakdown}`;
      }

      // Additions calculation
      if (row.additionsHistoryAmount && row.additionsHistoryAmount.length > 1) {
        const amountBreakdown = row.additionsHistoryAmount
          .map((amount) => formatAmount(amount, needRound))
          .join(" + ");
        reviewNotes += `\n${categoryName} (${typeLabel}) - Additions: ${formatAmount(
          row.additions,
          needRound
        )} = ${amountBreakdown}`;
      }

      // Disposal calculation
      if (row.disposalHistoryAmount && row.disposalHistoryAmount.length > 1) {
        const amountBreakdown = row.disposalHistoryAmount
          .map((amount) => formatAmount(amount, needRound))
          .join(" + ");
        reviewNotes += `\n${categoryName} (${typeLabel}) - Disposal: ${formatAmount(
          row.disposal,
          needRound
        )} = ${amountBreakdown}`;
      }
    });
  }

  return reviewNotes;
};

const applyTopBorder = (content) => {
  return content
    ? {
        content,
        styles: {
          fontStyle: "normal",
          lineWidth: {
            top: 0.1,
            left: 0,
            right: 0,
            bottom: 0,
          },
          lineColor: [0, 0, 0],
        },
      }
    : "";
};

const applyFaDisposalBorder = (content, isBold) => {
  return content
    ? {
        content,
        styles: {
          fontStyle: isBold ? "bold" : "normal",
          lineWidth: {
            top: isBold ? 0 : 0.1,
            left: 0,
            right: 0,
            bottom: isBold ? 0 : 0.1,
          },
          lineColor: [0, 0, 0],
        },
      }
    : "";
};

export const generateFaSummaryPdf = (
  forView,
  schedule,
  company,
  costRows,
  accDepreciationRows,
  totalOpening,
  totalAdditions,
  totalDisposal,
  totalClosing,
  notes,
  reviewNotes,
  isExcel = false
) => {
  const pdf = new BasePdfGenerator(false, isExcel);

  let contentColumns = [
    "Asset Category",
    "Opening Balance",
    "Additions",
    "Disposal",
    "Closing Balance",
  ];
  let totalLines = [];
  let totalDoubleLines = [];

  let contentData = [];
  let labelRow = ["", "$", "$", "$", "$"];
  contentData.push(labelRow);

  if (costRows && costRows.length > 0) {
    contentData.push([boldAutoColumn("Cost")]);

    costRows.map((row) => {
      let content = [
        row.assetCategory,
        formatAmount(row.openingBalance),
        formatAmount(row.additions),
        formatAmount(row.disposal),
        formatAmount(row.closingBalance),
      ];

      contentData.push(content);
    });

    contentData.push([
      boldAutoColumn("Total Cost"),
      formatAmount(totalOpening.cost),
      formatAmount(totalAdditions.cost),
      formatAmount(totalDisposal.cost),
      formatAmount(totalClosing.cost),
    ]);
    totalLines.push(contentData.length - 2);
    totalLines.push(contentData.length - 1);
    totalDoubleLines.push(contentData.length - 1);

    contentData.push([]);
  }

  if (accDepreciationRows && accDepreciationRows.length > 0) {
    contentData.push([boldAutoColumn("Accumulated Depreciation")]);

    accDepreciationRows.map((row) => {
      let content = [
        row.assetCategory,
        formatAmount(row.openingBalance),
        formatAmount(row.additions),
        formatAmount(row.disposal),
        formatAmount(row.closingBalance),
      ];

      contentData.push(content);
    });

    contentData.push([
      boldAutoColumn("Total Accumulated Depreciation"),
      formatAmount(totalOpening.accDepre),
      formatAmount(totalAdditions.accDepre),
      formatAmount(totalDisposal.accDepre),
      formatAmount(totalClosing.accDepre),
    ]);
    totalLines.push(contentData.length - 2);
    totalLines.push(contentData.length - 1);
    totalDoubleLines.push(contentData.length - 1);
  }

  const rowsNeedLines = [1, 2, 3, 4];
  if (contentData.length > 0)
    buildAutoTable({
      pdf,
      head: [contentColumns],
      body: contentData,
      schedule,
      company,
      columnStyles: {
        0: { halign: "left", minCellWidth: 60 },
        1: { halign: "right", cellWidth: 25 },
        2: { halign: "right", cellWidth: 25 },
        3: { halign: "right", cellWidth: 25 },
        4: { halign: "right", cellWidth: 25 },
      },
      didParseCell: function (data) {
        if (data.row.index === 0 && [0].includes(data.column.index)) {
          data.cell.styles.halign = "left";
        }
        if (data.row.index === 0 && rowsNeedLines.includes(data.column.index)) {
          data.cell.styles.halign = "center";
        }
      },
      didDrawCell: function (data) {
        beautyAutoLine(
          data,
          pdf.doc,
          (data.row.index === 0 && rowsNeedLines.includes(data.column.index)) ||
            (totalLines.includes(data.row.index) &&
              rowsNeedLines.includes(data.column.index)),
          totalDoubleLines.includes(data.row.index) &&
            rowsNeedLines.includes(data.column.index)
        );
      },
      lineSettings: {
        totalLines,
        totalDoubleLines,
        totalRows: rowsNeedLines,
        labelRows: rowsNeedLines,
      },
    });

  // Build calculator notes for FA Summary with specialized function
  const processedReviewNotes = buildFaSummaryCalculatorNotes(
    reviewNotes,
    costRows,
    accDepreciationRows
  );

  buildAutoNote(
    pdf,
    forView,
    company,
    schedule,
    notes,
    processedReviewNotes,
    [],
    footerSettings
  );

  return pdf.getDoc(forView, company, schedule);
};

export const generateFaRegisterPdf = (
  forView,
  schedule,
  company,
  assetRows,
  notes,
  reviewNotes,
  isExcel = false
) => {
  const pdf = new BasePdfGenerator(true, isExcel);

  const rows = Array.isArray(assetRows) ? assetRows : [];
  const amountFields = new Set([
    "costOfAsset",
    "twdvBf",
    "enhancedTwdvBf",
    "ia",
    "aa",
    "twdvCf",
  ]);
  const numberFields = new Set([
    "yaOfPurchase",
    "noOfYearsClaim",
    "noOfYearsBf",
    "noOfYearsCf",
    ...amountFields,
  ]);
  const centerFields = new Set(["deferred", "disposal"]);

  const toYesNo = (value) => {
    if (value === null || value === undefined) return "N.A.";
    return value ? "Yes" : "No";
  };

  const getPdfValue = (row, field) => {
    if (field === "calculationWarning") return row.calculationWarning || "";
    if (["deferred", "disposal"].includes(field)) {
      return toYesNo(row[field]);
    }

    const value = displayValue(row, field);
    if (value === "-" || value === "N.A." || value === "Disposed") return value;
    if (amountFields.has(field)) return formatAmount(row[field]);
    return `${value}`;
  };

  const getFieldLabel = (field) => {
    const labels = {
      assetCategory: "Category",
      assetDescription: "Description",
      yaOfPurchase: "YA",
      costOfAsset: "Cost",
      noOfYearsClaim: "Claim Yrs",
      noOfYearsBf: "Yrs B/F",
      noOfYearsCf: "Yrs C/F",
      twdvBf: "TWDV B/F",
      enhancedTwdvBf: "Enhanced TWDV B/F",
      twdvCf: "TWDV C/F",
      calculationWarning: "Warning",
      deferred: "Deferred?",
      disposal: "Disposed?",
    };
    if (labels[field]) return labels[field];
    return FIELD_LABELS[field] || field;
  };

  const hasFieldValue = (groupRows, field) =>
    groupRows.some((row) => {
      const value = row[field];
      return value !== null && value !== undefined && value !== "";
    });

  const getColumnsForGroup = (caType, groupRows) => {
    const common = [
      "assetCategory",
      "assetDescription",
      "yaOfPurchase",
      "costOfAsset",
    ];

    if (EXTERNAL_TYPES.has(caType)) {
      return [...common];
    }

    const columns = [
      ...common,
      "deferred",
      "disposal",
      "noOfYearsClaim",
      "noOfYearsBf",
      "twdvBf",
    ];

    if (hasFieldValue(groupRows, "enhancedTwdvBf")) {
      columns.push("enhancedTwdvBf");
    }

    if (hasFieldValue(groupRows, "iaRate")) {
      columns.push("iaRate", "ia");
    }

    columns.push("aa", "twdvCf", "noOfYearsCf");

    if (hasFieldValue(groupRows, "calculationWarning")) {
      columns.push("calculationWarning");
    }

    return columns;
  };

  const getCellWidth = (field, columnCount) => {
    const compact = columnCount >= 13;
    const widths = {
      assetCategory: compact ? 38 : 40,
      assetDescription: 42,
      yaOfPurchase: compact ? 12 : 14,
      costOfAsset: compact ? 18 : 22,
      deferred: 20,
      disposal: 21,
      noOfYearsClaim: 15,
      noOfYearsBf: 15,
      noOfYearsCf: 15,
      twdvBf: compact ? 18 : 22,
      enhancedTwdvBf: compact ? 20 : 24,
      iaRate: compact ? 12 : 15,
      ia: compact ? 18 : 22,
      aa: compact ? 18 : 22,
      twdvCf: compact ? 18 : 22,
      calculationWarning: compact ? 34 : 42,
    };

    if (columnCount <= 5) {
      return {
        assetCategory: 40,
        assetDescription: 60,
        yaOfPurchase: 20,
        costOfAsset: 30,
      };
    }

    return widths[field];
  };

  const getColumnStyles = (columns) =>
    columns.reduce((acc, field, index) => {
      acc[index] = {
        halign:
          amountFields.has(field) || numberFields.has(field) ? "right" : "left",
        cellWidth: getCellWidth(field, columns.length),
      };

      if (centerFields.has(field)) acc[index].halign = "center";
      if (field === "assetDescription" || field === "calculationWarning") {
        acc[index].halign = "left";
      }

      if (
        [
          "yaOfPurchase",
          "noOfYearsClaim",
          "noOfYearsBf",
          "noOfYearsCf",
          "iaRate",
        ].includes(field)
      ) {
        acc[index].halign = "center";
      }

      return acc;
    }, {});

  const buildTotalRow = (groupRows, columns) =>
    columns.map((field, index) => {
      if (index === 0) return boldAutoColumn("Total");
      if (!amountFields.has(field)) return "";

      const total = groupRows.reduce((sum, row) => {
        const value = Number(row[field]);
        return Number.isFinite(value) ? sum + value : sum;
      }, 0);

      return boldAutoColumn(formatAmount(total));
    });

  const rowsWithIndex = rows.map((row, index) => ({
    ...row,
    assetIndex: index + 1,
  }));

  const groupedRows = rowsWithIndex.reduce((map, row) => {
    const key = row.caType || "Unassigned";
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(row);
    return map;
  }, new Map());

  if (rows.length === 0) {
    buildAutoTable({
      pdf,
      head: [["Fixed Asset Register"]],
      body: [["No assets defined."]],
      schedule,
      company,
      customStartY: 65,
      checkHeaderCondition: true,
      printFooter: true,
      lineSettings: {},
    });
  }

  Array.from(groupedRows.entries()).forEach(([caType, groupRows], index) => {
    if (!pdf._meta) {
      pdf._meta = {
        footers: new Set(),
        curRef: null,
      };
    }
    pdf._meta.curRef = `FA Register | ${caType}`;

    const columns = getColumnsForGroup(caType, groupRows);
    const body = groupRows.map((row) =>
      columns.map((field) => getPdfValue(row, field))
    );
    const totalRowIndex = body.length;
    const totalRows = columns
      .map((field, columnIndex) =>
        amountFields.has(field) ? columnIndex : null
      )
      .filter((columnIndex) => columnIndex !== null);

    if (totalRows.length > 0) body.push(buildTotalRow(groupRows, columns));

    buildAutoTable({
      pdf,
      head: [columns.map(getFieldLabel)],
      body,
      schedule,
      company,
      customStartY: index === 0 ? 65 : pdf.startY + 8,
      addPlainTitle: true,
      tableTitle: `${caType} (${groupRows.length} ${
        groupRows.length === 1 ? "asset" : "assets"
      })`,
      checkHeaderCondition: true,
      columnStyles: getColumnStyles(columns),
      styles: {
        cellPadding: { top: 1.5, right: 1.5, bottom: 1.5, left: 1.5 },
        overflow: "linebreak",
      },
      didDrawCell: function (data) {
        beautyAutoLine(
          data,
          pdf.doc,
          (data.row.index === totalRowIndex - 1 &&
            totalRows.includes(data.column.index)) ||
            (data.row.index === totalRowIndex &&
              totalRows.includes(data.column.index)),
          data.row.index === totalRowIndex &&
            totalRows.includes(data.column.index)
        );
      },
      lineSettings: {
        totalLines: totalRows.length > 0 ? [totalRowIndex] : [],
        totalDoubleLines: totalRows.length > 0 ? [totalRowIndex] : [],
        totalRows,
      },
    });
  });

  if (rows.length > 0) pdf._meta.curRef = "Notes";
  buildAutoNote(
    pdf,
    forView,
    company,
    schedule,
    notes,
    reviewNotes,
    rows,
    footerSettings
  );

  return pdf.getDoc(forView, company, schedule);
};

export const generateFaAdditionsPdf = (
  forView,
  schedule,
  company,
  assetLines,
  otherSources,
  registeredAssetSources,
  totalLines,
  totalAdditions,
  otherSourcesTotalLines,
  registeredAssetSourcesTotalLines,
  combinedTotalLines,
  filteredUsedColumns,
  caSettings,
  refSources,
  notes,
  reviewNotes,
  renovationScheduleNo,
  isExcel = false
) => {
  const pdf = new BasePdfGenerator(true, isExcel);
  const hasOtherSources =
    otherSources &&
    otherSources.additionsDetails &&
    otherSources.additionsDetails.length > 0;
  const registeredSourceCategories = (() => {
    const additionsDetails = registeredAssetSources?.additionsDetails || [];
    const categories = Array.isArray(registeredAssetSources?.categories)
      ? registeredAssetSources.categories
      : [];

    if (categories.length > 0) return categories;
    if (additionsDetails.length === 0) return [];

    const cost = additionsDetails.reduce(
      (total, detail) => total + (Number(detail.cost) || 0),
      0
    );

    return [
      {
        assetCategory: "Registered Assets",
        additionsDetails,
        totalFloating: cost,
        cost: { additions: cost },
        isCostAligned: true,
      },
    ];
  })();
  const hasRegisteredAssetSources = registeredSourceCategories.length > 0;
  const hasAssetLineDetails =
    Array.isArray(assetLines) &&
    assetLines.some((category) => category.additionsDetails?.length > 0);

  const applyLine = (content, isBold) => {
    return content
      ? {
          content,
          styles: {
            fontStyle: isBold ? "bold" : "normal",
            lineWidth: {
              top: isBold ? 0 : 0.1,
              left: 0,
              right: 0,
              bottom: isBold ? 0 : 0.1,
            },
            lineColor: [0, 0, 0],
          },
        }
      : "";
  };

  let contentColumns = ["Description", "Cost", "Defer?", "No. of Yrs"];

  if (forView) {
    contentColumns.push(autoTextColorNoBreak("Classification"));
  }

  let contentData = [];
  let rowsNeedLine = [];

  let excelCostNeedLine = [];
  let excelRowsNeedLine = [];
  let excelRowsNeedDoubleLine = [];

  let fixedColLength = contentColumns.length;

  filteredUsedColumns.forEach((col, index) => {
    contentColumns.push(col);

    rowsNeedLine.push(fixedColLength + index);
  });

  if (hasAssetLineDetails) {
    let colLength = fixedColLength + rowsNeedLine.length;

    assetLines.map((category) => {
      if (category.additionsDetails.length <= 0) {
        return;
      }

      let catData = [
        {
          content: category.assetCategory,
          colSpan: colLength,
          styles: {
            fontStyle: "bold",
            halign: "left",
          },
        },
      ];
      contentData.push(catData);

      category.additionsDetails.map((row) => {
        let content = [
          row.description,
          formatAmount(row.cost),
          row.isDeferred ? "Yes" : "No",
          formatAmount(row.numberOfYears) || 0,
        ];
        if (forView) content.push(autoTextColorNoBreak(row.CaName));

        filteredUsedColumns.map((caName, index) => {
          if (row.CaName === caName) {
            content.push(formatAmount(row.cost));
          } else content.push("");
        });

        contentData.push(content);
      });

      let total = [
        "",
        applyLine(formatAmount(category.totalFloating)),
        "",
        "",
        "",
      ];
      let check = [
        "",
        forView
          ? autoTextColor(
              category.isCostAligned ? "Checked" : "Not Matched",
              category.isCostAligned ? [0, 128, 0] : [255, 0, 0]
            )
          : "",
        "",
        "",
        "",
      ];

      contentData.push(total);
      excelCostNeedLine.push(contentData.length - 2);
      if (forView) contentData.push(check);
    });

    let totalLabel = "";
    if (!hasRegisteredAssetSources) {
      totalLabel = hasOtherSources ? "Subtotal" : "Total";
    }

    if (!hasRegisteredAssetSources) {
      let total = ["", "", "", boldAutoColumn(totalLabel)];

      if (forView) total = ["", "", "", "", boldAutoColumn(totalLabel)];

      filteredUsedColumns.forEach((caName) => {
        total.push(applyLine(formatAmount(totalLines[caName]) || ""));
      });

      excelRowsNeedLine.push(contentData.length - 1);

      contentData.push(total);
    }
  }

  if (hasRegisteredAssetSources) {
    let colLength = fixedColLength + rowsNeedLine.length;

    registeredSourceCategories.map((category) => {
      const categoryDetails = category.additionsDetails || [];
      if (categoryDetails.length <= 0) return;

      let catData = [
        {
          content: category.assetCategory || "Registered Assets",
          colSpan: colLength,
          styles: {
            fontStyle: "bold",
            halign: "left",
          },
        },
      ];

      contentData.push(catData);

      categoryDetails.map((row) => {
        let content = [
          row.description,
          formatAmount(row.cost),
          row.isDeferred ? "Yes" : "No",
          formatAmount(row.numberOfYears) || 0,
        ];
        if (forView) content.push(autoTextColorNoBreak(row.CaName));

        filteredUsedColumns.map((caName, index) => {
          if (row.CaName === caName) {
            content.push(formatAmount(row.cost));
          } else content.push("");
        });

        contentData.push(content);
      });

      const categoryTotal = categoryDetails.reduce(
        (total, detail) => total + (Number(detail.cost) || 0),
        0
      );
      const expectedTotal = Number(category.cost?.additions) || categoryTotal;
      const isCostAligned =
        category.isCostAligned !== undefined
          ? category.isCostAligned
          : categoryTotal === expectedTotal;
      let total = [
        "",
        applyLine(formatAmount(category.totalFloating ?? categoryTotal)),
        "",
        "",
        "",
      ];
      let check = [
        "",
        forView
          ? autoTextColor(
              isCostAligned ? "Checked" : "Not Matched",
              isCostAligned ? [0, 128, 0] : [255, 0, 0]
            )
          : "",
        "",
        "",
        "",
      ];

      contentData.push(total);
      excelCostNeedLine.push(contentData.length - 2);
      if (forView) contentData.push(check);
    });

    if (hasOtherSources) {
      const subtotalCost =
        (totalLines["totalCost"] || 0) + (registeredAssetSources.cost || 0);
      let total = ["", applyLine(formatAmount(subtotalCost)), ""];

      if (forView) total.push("");

      total.push(boldAutoColumn("Subtotal"));

      filteredUsedColumns.forEach((caName, index) => {
        const subtotalLine =
          (totalLines[caName] || 0) +
          (registeredAssetSourcesTotalLines[caName] || 0);
        total.push(applyLine(formatAmount(subtotalLine) || ""));
      });

      contentData.push(total);
      excelCostNeedLine.push(contentData.length - 2);
      excelRowsNeedLine.push(contentData.length - 2);
      if (!isExcel) contentData.push([]);
    }

    if (!hasOtherSources) {
      let costTotal =
        (totalLines["totalCost"] || 0) + (registeredAssetSources.cost || 0);
      let final = ["", applyLine(formatAmount(costTotal)), ""];

      if (forView) final.push("");

      final.push(boldAutoColumn("Total"));

      let reference = ["", "", "", ""];

      if (forView) reference.push("");

      const sourceMapping = {
        S16: "S16Schedule",
        S19B: "S19BSchedule",
        S19D: "S19DSchedule",
        S19: "S19Schedule",
        S19A: "S19ASchedule",
      };

      filteredUsedColumns.forEach((caName) => {
        final.push(applyLine(formatAmount(combinedTotalLines[caName]) || ""));

        if (caName === "Renovation")
          reference.push(
            autoTextColor(`Sch ${refSources["Renovation"]}`, [0, 0, 0])
          );
        else if (caName === "Revenue")
          reference.push(autoTextColorNoBreak("Deduct / Main Comp", [0, 0, 0]));
        else {
          const scheduleDetails = getCaSettingByKey(caSettings, caName);
          if (
            scheduleDetails &&
            scheduleDetails.caType &&
            refSources[sourceMapping[scheduleDetails.caType]]
          ) {
            reference.push(
              `Sch ${refSources[sourceMapping[scheduleDetails.caType]]}`
            );
          } else reference.push("");
        }
      });

      excelCostNeedLine.push(contentData.length - 1);
      excelRowsNeedLine.push(contentData.length - 1);
      excelRowsNeedDoubleLine.push(contentData.length);
      contentData.push(final);
      contentData.push(reference);
    }
  }

  if (hasOtherSources) {
    let colLength = fixedColLength + rowsNeedLine.length;
    let catData = [
      {
        content: "P&L Schedules",
        colSpan: colLength,
        styles: {
          fontStyle: "bold",
          halign: "left",
        },
      },
    ];

    contentData.push(catData);

    otherSources.additionsDetails.map((row) => {
      let content = [
        `${row.description} - ${
          isAutoload(row.status) ? row.source.sch_no : row?.reference
        }`,
        formatAmount(row.cost),
        row.isDeferred ? "Yes" : "No",
        formatAmount(row.numberOfYears) || 0,
      ];
      if (forView) content.push(autoTextColorNoBreak(row.CaName));

      filteredUsedColumns.map((caName, index) => {
        if (row.CaName === caName) {
          content.push(formatAmount(row.cost));
        } else content.push("");
      });

      contentData.push(content);
    });

    if (hasRegisteredAssetSources || hasAssetLineDetails) {
      let total = ["", applyLine(formatAmount(otherSources.cost)), ""];

      if (forView) total.push("");

      total.push(boldAutoColumn("Subtotal"));

      filteredUsedColumns.forEach((caName, index) => {
        total.push(
          applyLine(formatAmount(otherSourcesTotalLines[caName]) || "")
        );
      });

      contentData.push(total);
      excelCostNeedLine.push(contentData.length - 2);
      excelRowsNeedLine.push(contentData.length - 2);
      if (!isExcel) contentData.push([]);
    }
    let costTotal =
      (totalLines["totalCost"] || 0) +
      (otherSources.cost || 0) +
      (registeredAssetSources?.cost || 0);
    let final = ["", applyLine(formatAmount(costTotal)), ""];

    if (forView) final.push("");

    final.push(boldAutoColumn("Total"));

    let reference = ["", "", "", ""];

    if (forView) reference.push("");

    const sourceMapping = {
      S16: "S16Schedule",
      S19B: "S19BSchedule",
      S19D: "S19DSchedule",
      S19: "S19Schedule",
      S19A: "S19ASchedule",
    };

    filteredUsedColumns.forEach((caName) => {
      final.push(applyLine(formatAmount(combinedTotalLines[caName]) || ""));

      if (caName === "Renovation")
        reference.push(
          autoTextColor(`Sch ${refSources["Renovation"]}`, [0, 0, 0])
        );
      else if (caName === "Revenue")
        reference.push(autoTextColorNoBreak("Deduct / Main Comp", [0, 0, 0]));
      else {
        const scheduleDetails = getCaSettingByKey(caSettings, caName);
        if (
          scheduleDetails &&
          scheduleDetails.caType &&
          refSources[sourceMapping[scheduleDetails.caType]]
        ) {
          reference.push(
            `Sch ${refSources[sourceMapping[scheduleDetails.caType]]}`
          );
        } else reference.push("");
      }
    });

    excelCostNeedLine.push(contentData.length - 1);
    excelRowsNeedLine.push(contentData.length - 1);
    excelRowsNeedDoubleLine.push(contentData.length);
    contentData.push(final);
    contentData.push(reference);
  }

  if (contentData.length >= 0) {
    let columnSettings = {
      0: { halign: "left" },
      1: { halign: "center" },
      2: { halign: "center" },
      3: { halign: "center" },
    };

    if (forView) {
      columnSettings[4] = {
        halign: "center",
      };
    } else {
      columnSettings[4] = { halign: "right" };
    }

    rowsNeedLine.forEach((row) => {
      columnSettings[row] = {
        halign: "right",
        minCellWidth: 18,
      };
    });

    if (rowsNeedLine.length > 8) pdf.fs = 8;

    buildAutoTable({
      pdf,
      head: [contentColumns],
      body: contentData,
      schedule,
      company,
      columnStyles: columnSettings,
      didParseCell: function (data) {
        if (data.row.index === 0) {
          data.cell.styles.halign = "left";

          if ([1].includes(data.column.index)) {
            data.cell.styles.halign = "center";
          }

          if ([2, 3].includes(data.column.index)) {
            data.cell.styles.halign = "center";
          }

          if ([4].includes(data.column.index)) {
            data.cell.styles.halign = forView ? "center" : "right";
          }

          if (rowsNeedLine.includes(data.column.index)) {
            data.cell.styles.halign = "center";
          }
        }

        if (data.row.index === 0 && rowsNeedLine.includes(data.column.index)) {
        }

        if (data.row.index !== 0 && rowsNeedLine.includes(data.column.index)) {
          data.cell.styles.halign = "right";
        }
      },
      lineSettings: {
        totalLines: excelRowsNeedLine,
        subTotalLines: excelCostNeedLine,
        totalDoubleLines: excelRowsNeedDoubleLine,
        totalRows: rowsNeedLine,
        labelRows: rowsNeedLine,
        subTotalRows: [1],
      },
    });
  }

  buildAutoNote(
    pdf,
    forView,
    company,
    schedule,
    notes,
    reviewNotes,
    [],
    footerSettings
  );
  return pdf.getDoc(forView, company, schedule);
};

export const generateFaDisposalPdf = (
  forView,
  schedule,
  company,
  assetLines,
  totalAssetLines,
  showNetbookValue,
  twdvSummary,
  bcSummary,
  refSources,
  notes,
  reviewNotes,
  isExcel = false
) => {
  const pdf = new BasePdfGenerator(true, isExcel);
  pdf.fs = 9;

  const allNotHirePurchase = assetLines
    .flatMap((asset) => asset.disposalDetails)
    .every((detail) => !detail.isHirePurchase);

  const headerSettings = ["Description of Asset", "CA Name", "CA Summary"];

  if (!allNotHirePurchase) {
    headerSettings.push("HP?");
  }

  headerSettings.push(
    "YA of purchase",
    "Fully claimed?",
    "No. of years b/f",
    "Cost"
  );

  if (!allNotHirePurchase) {
    headerSettings.push("HP Principal Paid");
  }

  headerSettings.push(
    "TWDV b/f\n(a)",
    "Sales Proceeds\n(b)",
    "BA / (BC)\n(c) = (a) - (b)"
  );

  if (showNetbookValue) {
    headerSettings.push("NBV (d)");
    headerSettings.push("Gain / (loss) on disposal\n(e) = (b) - (d)");
  }
  let excelNeedLineRows = [6, 7, 8, 9, 10, 11, 12];
  let totalLines = [];
  let totalDoubleLines = [];

  const tableBody = [];

  assetLines.forEach((asset) => {
    const categoryRow = [
      {
        content: asset.assetCategory,
        styles: {
          fontStyle: "bold",
          halign: "left",
        },
      },
    ];
    tableBody.push(categoryRow);

    asset.disposalDetails.forEach((detail) => {
      let content = [detail.description, detail.CaName];

      if (["S16 - LIA", "S16 - IBA"].includes(detail.CaName)) {
        content.push(detail.CaName);
      } else content.push(detail.CaSummary);

      if (!allNotHirePurchase) {
        content.push(detail.isHirePurchase ? "Yes" : "No");
      }

      content.push(
        `${detail.yaOfPurchase}`,
        detail.isFullyClaimed ? "Yes" : "No",
        detail.isFullyClaimed
          ? "Fully Claimed"
          : detail.isHirePurchase
            ? "Various"
            : formatAmount(detail.noOfYearsBf),
        formatAmount(detail.costInDetails)
      );

      if (!allNotHirePurchase) {
        content.push(formatAmount(detail.hpPrincipalPaid));
      }

      content.push(
        formatAmount(detail.twdvBf),
        formatAmount(detail.salesProceeds),
        formatAmount(detail.bcBa)
      );

      if (showNetbookValue) {
        content.push(
          formatAmount(detail.netBookValue),
          formatAmount(detail.disposalGainLoss)
        );
      }
      tableBody.push(content);
    });

    let assetTotal = totalAssetLines.dataRows.find(
      (total) => total.assetId === asset.assetId
    );

    let totalContent = [
      applyFaDisposalBorder(""),
      applyFaDisposalBorder(""),
      applyFaDisposalBorder(""),
    ];

    if (!allNotHirePurchase) {
      totalContent.push(applyFaDisposalBorder(""));
    }

    totalContent.push(
      applyFaDisposalBorder(""),
      applyFaDisposalBorder(""),
      applyFaDisposalBorder("Sub Total", true),
      applyFaDisposalBorder(formatAmount(assetTotal.totalDetails.costInDetails))
    );

    if (!allNotHirePurchase) {
      totalContent.push(
        applyFaDisposalBorder(
          formatAmount(assetTotal.totalDetails.hpPrincipalPaid)
        )
      );
    }

    totalContent.push(
      applyFaDisposalBorder(formatAmount(assetTotal.totalDetails.twdvBf)),
      applyFaDisposalBorder(
        formatAmount(assetTotal.totalDetails.salesProceeds)
      ),
      applyFaDisposalBorder(formatAmount(assetTotal.totalDetails.bcBa))
    );

    if (showNetbookValue) {
      totalContent.push(
        applyFaDisposalBorder(
          formatAmount(assetTotal.totalDetails.netBookValue)
        ),
        applyFaDisposalBorder(
          formatAmount(assetTotal.totalDetails.disposalGainLoss)
        )
      );
    }

    tableBody.push(totalContent);
    totalLines.push(tableBody.length - 2);
    if (forView) {
      let checkedContent = ["", "", ""];

      if (!allNotHirePurchase) {
        checkedContent.push("");
      }

      checkedContent.push(
        "",
        "",
        "",
        autoTextColor(
          assetTotal.costCheck ? "Checked" : "Not Matched",
          undefined,
          undefined,
          { top: 1, bottom: 0 }
        )
      );

      tableBody.push(checkedContent);
    } else tableBody.push([]);
  });

  let finalTotalContent = [
    applyFaDisposalBorder(""),
    applyFaDisposalBorder(""),
    applyFaDisposalBorder(""),
  ];

  if (!allNotHirePurchase) {
    finalTotalContent.push(applyFaDisposalBorder(""));
  }

  finalTotalContent.push(
    applyFaDisposalBorder(""),
    applyFaDisposalBorder(""),
    applyFaDisposalBorder("Total", true),
    applyFaDisposalBorder(
      formatAmount(totalAssetLines.overallTotalDetails.costInDetails)
    )
  );

  if (!allNotHirePurchase) {
    finalTotalContent.push(
      applyFaDisposalBorder(
        formatAmount(totalAssetLines.overallTotalDetails.hpPrincipalPaid)
      )
    );
  }

  finalTotalContent.push(
    applyFaDisposalBorder(
      formatAmount(totalAssetLines.overallTotalDetails.twdvBf)
    ),
    applyFaDisposalBorder(
      formatAmount(totalAssetLines.overallTotalDetails.salesProceeds)
    ),
    applyFaDisposalBorder(
      formatAmount(totalAssetLines.overallTotalDetails.bcBa)
    )
  );

  if (showNetbookValue) {
    finalTotalContent.push(
      applyFaDisposalBorder(
        formatAmount(totalAssetLines.overallTotalDetails.netBookValue)
      ),
      applyFaDisposalBorder(
        formatAmount(totalAssetLines.overallTotalDetails.disposalGainLoss)
      )
    );
  }

  tableBody.push(finalTotalContent);
  totalLines.push(tableBody.length - 2);
  totalDoubleLines.push(tableBody.length - 1);

  const baBcColumnIndex = headerSettings.findIndex((header) =>
    String(header).includes("BA / (BC)")
  );
  const netBcBa = Number(totalAssetLines.overallTotalDetails.bcBa) || 0;
  const caSummaryScheduleNo = refSources?.CASummary;
  const baBcReference =
    netBcBa < 0
      ? "Main Comp"
      : netBcBa > 0 && caSummaryScheduleNo
        ? `Sch ${caSummaryScheduleNo}`
        : "";

  if (baBcReference && baBcColumnIndex >= 0) {
    const referenceContent = finalTotalContent.map(() => "");
    referenceContent[baBcColumnIndex] = forView
      ? autoTextColor(baBcReference, [0, 0, 0])
      : baBcReference;
    tableBody.push(referenceContent);
  }

  if (tableBody.length >= 0) {
    buildAutoTable({
      pdf,
      head: [headerSettings],
      body: tableBody,
      schedule,
      company,
      columnStyles: {
        0: { halign: "left", minCellWidth: 20 },
        1: { halign: "center", cellWidth: !showNetbookValue ? 27 : 20 },
        2: { halign: "center", cellWidth: allNotHirePurchase ? 20 : 17 },
        3: { halign: "center", cellWidth: allNotHirePurchase ? 18 : 15 },
        4: { halign: "center", cellWidth: 18 },
        5: { halign: "center", cellWidth: allNotHirePurchase ? 21 : 17 },
        6: {
          halign: allNotHirePurchase ? "right" : "center",
          cellWidth: allNotHirePurchase ? 20 : 21,
        },
        7: { halign: "right", cellWidth: 20 },
        8: { halign: "right", cellWidth: 20 },
        9: { halign: "right", cellWidth: 20 },
        10: { halign: "right", cellWidth: 20 },
        11: { halign: "right", cellWidth: 20 },
        12: { halign: "right", cellWidth: 20 },
        13: { halign: "right", cellWidth: 20 },
      },
      didParseCell: function (data) {
        if (data.row.index === 0) {
          if ([0].includes(data.column.index)) {
            data.cell.styles.halign = "left";
          }
        }
      },
      lineSettings: {
        totalLines: totalLines,
        totalDoubleLines: totalDoubleLines,
        totalRows: excelNeedLineRows,
      },
    });
  }

  if (!isExcel) pdf.startY = pdf.doc.lastAutoTable.finalY + 10;

  if (
    twdvSummary &&
    twdvSummary.length > 0 &&
    bcSummary &&
    bcSummary.length > 0 &&
    forView
  ) {
    // TWDV Summary Table
    const twdvHeaders = [
      "CA Name",
      "YA of Purchase",
      "No. of Years b/f",
      "Hire Purchase",
      "TWDV",
    ];
    const twdvBody = twdvSummary.map((row) => [
      row.caName,
      `${row.yaOfPurchase}`,
      `${row.noOfYearsBf}`,
      row.isHirePurchase ? "Yes" : "No",
      row.twdv.toLocaleString(),
    ]);

    twdvBody.push([
      {
        content: "Total",
        colSpan: 4,
        styles: { halign: "right", fontStyle: "bold" },
      },
      {
        content: twdvSummary
          .reduce((acc, row) => acc + row.twdv, 0)
          .toLocaleString(),
        styles: { fontStyle: "bold" },
      },
    ]);

    let headStyles = {
      fontStyle: "bold",
      halign: "center",
      cellPadding: { left: 5, right: 3, top: 3, bottom: 3 },
      lineColor: [255, 0, 0],
      textColor: [255, 0, 0],
    };
    let styles = {
      fontSize: 9,
      lineWidth: 0.1,
      lineColor: [255, 0, 0],
      textColor: [255, 0, 0],
    };

    buildAutoTable({
      pdf,
      head: [twdvHeaders],
      body: twdvBody,
      checkHeaderCondition: true,
      customStartY: pdf.startY + 10,
      addTableTitle: true,
      tableTitle: "TWDV Summary",
      schedule,
      company,
      headStyles: headStyles,
      styles: styles,
      columnStyles: {
        0: {
          halign: "left",
          cellPadding: { left: 5, right: 3, top: 3, bottom: 3 },
        },
        1: {
          halign: "center",
          cellPadding: { left: 5, right: 3, top: 3, bottom: 3 },
        },
        2: {
          halign: "center",
          cellPadding: { left: 5, right: 3, top: 3, bottom: 3 },
        },
        3: {
          halign: "center",
          cellPadding: { left: 5, right: 3, top: 3, bottom: 3 },
        },
        4: {
          halign: "right",
          cellPadding: { left: 5, right: 3, top: 3, bottom: 3 },
        },
      },
      lineSettings: {
        totalLines: [],
        subTotalLines: [twdvBody.length - 2],
        totalDoubleLines: [twdvBody.length - 1],
        totalRows: [1],
        subTotalRows: [4],
      },
    });

    if (!isExcel) pdf.startY = pdf.doc.lastAutoTable.finalY + 10;

    const bcHeaders = ["Adjustment", "BA / (BC)"];
    const bcBody = bcSummary.map((row) => [
      row.caName,
      row.bcBa < 0
        ? `(${Math.abs(row.bcBa).toLocaleString()})`
        : row.bcBa.toLocaleString(),
    ]);

    // Add total row to BC summary
    const totalBC = bcSummary.reduce((acc, row) => acc + row.bcBa, 0);
    bcBody.push([
      { content: "Total", styles: { halign: "right", fontStyle: "bold" } },
      {
        content:
          totalBC < 0
            ? `(${Math.abs(totalBC).toLocaleString()})`
            : totalBC.toLocaleString(),
        styles: { fontStyle: "bold" },
      },
    ]);

    buildAutoTable({
      pdf,
      head: [bcHeaders],
      body: bcBody,
      checkHeaderCondition: true,
      customStartY: pdf.startY + 10,
      addTableTitle: true,
      tableTitle: "BC Summary",
      schedule,
      company,
      columnStyles: {
        0: {
          halign: "left",
          cellPadding: { left: 5, right: 3, top: 3, bottom: 3 },
        },
        1: {
          halign: "right",
          cellPadding: { left: 5, right: 3, top: 3, bottom: 3 },
        },
      },
      headStyles: headStyles,
      styles: styles,
      lineSettings: {
        totalLines: [],
        subTotalLines: [twdvBody.length - 2],
        totalDoubleLines: [twdvBody.length - 1],
        totalRows: [1],
        subTotalRows: [1],
      },
    });

    if (!isExcel) pdf.startY = pdf.doc.lastAutoTable.finalY + 10;
  }

  buildAutoNote(
    pdf,
    forView,
    company,
    schedule,
    notes,
    reviewNotes,
    [],
    footerSettings
  );
  return pdf.getDoc(forView, company, schedule);
};

export const generateCaSchedulePdf = (
  forView,
  schedule,
  company,
  caType,
  refSources,
  additionsDetails,
  disposalDetails,
  hirePurchaseAdditions,
  hirePurchaseDisposal,
  totalAdditionsDetails,
  totalDisposalDetails,
  totalHpAdditionsDetails,
  totalHpDisposalDetails,
  totalNetAA,
  caSummaryDetails,
  notes,
  reviewNotes,
  isExcel = false
) => {
  const pdf = new BasePdfGenerator(true, isExcel);

  const hasAllowance = disposalDetails?.length || additionsDetails?.length;
  const hasHp =
    (hirePurchaseAdditions && hirePurchaseAdditions.length > 0) ||
    (hirePurchaseDisposal && hirePurchaseDisposal.length > 0);
  const getRef = (keyMap, key, value) => {
    const refKey =
      typeof keyMap[key] === "string" ? keyMap[key] : keyMap[key]?.[value];

    return refKey && refSources[refKey]
      ? lineBreakText(`Sch ${refSources[refKey]}`, {
          top: 3,
          bottom: 0,
          right: 1,
        })
      : "";
  };

  let headers = headerSettings.filter((item) => {
    if (caType === "S16") {
      if (item.value === "CA Name") return false;
    }

    if (!item.value) return false;
    if (!item.visibility.includes(caType)) return false;
    if (item.value === "Current Year Disposal") return false;

    if (item.value === "Principal Paid" && !hasHp) {
      return false;
    }

    if (item.value === "Deferred" || item.value === "Disregard") return false;

    return true;
  });

  let totalLines = [];
  let totalDoubleLines = [];

  let totalDisregardLines = [];
  let totalDisregardDoubleLines = [];

  let needLines = [];
  let needS16DoubleLines = [];
  let ignoreLines = [];

  let contentData = [];

  const processRow = (row, type) => {
    let content = [];

    content.push(getCaSchedulePdfYa(row));

    let hasEnhanced = false;
    headers.forEach((h, i) => {
      if (
        [
          "initialAllowance",
          "annualAllowance",
          "twdvBf",
          "twdvCf",
          "currentAmount",
          "totalAmount",
          "principalPaid",
          "cost",
        ].includes(h.key)
      ) {
        if (["initialAllowance", "annualAllowance"].includes(h.key)) {
          content.push(
            h.key === "annualAllowance" &&
              caType === "S19D" &&
              row.assetInUseAtFYE === false
              ? "N.A"
              : row.isDeferred &&
                  !(
                    row.isHirePurchase &&
                    !row.manualData &&
                    row.caName === "S19" &&
                    h.key === "initialAllowance"
                  )
                ? "Deferred"
                : formatAmount(row[h.key])
          );
        } else if (h.key === "currentAmount") {
          if (type === "disposal") {
            if (h.value === "Additions") content.push("0");
            else content.push(formatAmount(row[h.key]));
          } else {
            if (h.value === "Additions") content.push(formatAmount(row[h.key]));
            else content.push(formatAmount(row["currentYearDisposal"]));
          }
        } else if (h.key === "principalPaid") {
          if (!row[h.key]) content.push("");
          else content.push(formatAmount(row[h.key]));
        } else {
          content.push(formatAmount(row[h.key]));
        }
      } else if (["initialAllowanceRate"].includes(h.key)) {
        content.push(`${row[h.key]}` || "0");
      } else if (h.key === "assetInUseAtFYE") {
        content.push(row.assetInUseAtFYE === false ? "No" : "Yes");
      } else if (["isDeferred", "isDisregard"].includes(h.key)) {
        content.push(row[h.key] ? "Yes" : "No");
      } else if (["allowanceType"].includes(h.key)) {
        if (row["allowanceType"] === "Enhanced") {
          hasEnhanced = true;
          content.push("Base");
        } else content.push(`${row[h.key]}`);
      } else if (["caSummary"].includes(h.key)) {
        if (
          !["S16 - (LIA / IBA)", "S16 - LIA", "S16 - IBA"].includes(
            row["caName"]
          )
        )
          content.push(row.caSummary);
        else if (["S16 - LIA", "S16 - IBA"].includes(row["caName"]))
          content.push(row.caName);
        else content.push(row.s16Type);
      } else if (["caName"].includes(h.key)) {
        if (["S16 - LIA", "S16 - IBA"].includes(row["caName"]))
          content.push("S16 - (LIA / IBA)");
        else content.push(row.caName);
      } else {
        content.push(`${row[h.key]}`);
      }
    });

    contentData.push(content);

    if (hasEnhanced) {
      content = [getCaSchedulePdfYa(row)];
      headers.forEach((h, i) => {
        if (
          [
            "initialAllowance",
            "annualAllowance",
            "twdvBf",
            "twdvCf",
            "currentAmount",
            "totalAmount",
            "principalPaid",
          ].includes(h.key)
        ) {
          if (["initialAllowance", "annualAllowance"].includes(h.key)) {
            content.push(
              row.isDeferred ? "Deferred" : formatAmount(row.enhanced[h.key])
            );
          } else if (h.key === "currentAmount") {
            if (type === "disposal") {
              if (h.value === "Additions") content.push("0");
              else content.push(formatAmount(row.enhanced[h.key]));
            } else {
              if (h.value === "Additions")
                content.push(formatAmount(row.enhanced[h.key]));
              else
                content.push(formatAmount(row.enhanced["currentYearDisposal"]));
            }
          } else if (h.key === "twdvBf") {
            content.push(formatAmount(row.enhanced[h.key]));
          } else if (h.key === "principalPaid") {
            if (!row.enhanced[h.key]) content.push("");
            else content.push(formatAmount(row.enhanced[h.key]));
          } else {
            content.push(formatAmount(row.enhanced[h.key]));
          }
        } else if (["isDeferred", "isDisregard"].includes(h.key)) {
          content.push(row[h.key] ? "Yes" : "No");
        } else if (["caSummary"].includes(h.key)) {
          if (["S16 - LIA", "S16 - IBA"].includes(row["caName"]))
            content.push(row.caName);
          else content.push(row.s16Type);
        } else if (["caName"].includes(h.key)) {
          if (["S16 - LIA", "S16 - IBA"].includes(row["caName"]))
            content.push("S16 - (LIA / IBA)");
          else content.push(row.caName);
        } else {
          content.push(`${row[h.key]}`);
        }
      });

      contentData.push(content);
    }
  };

  let totalIndex = 0;
  const stopIndex = 2;
  const processTotal = (addition, disposal) => {
    let totalContent = [""];

    let needAdd = false;
    headers.forEach((h, i) => {
      if (
        [
          "initialAllowance",
          "annualAllowance",
          "twdvBf",
          "twdvCf",
          "currentAmount",
          "totalAmount",
        ].includes(h.key)
      ) {
        if (
          caType === "S16" &&
          ["initialAllowance", "annualAllowance"].includes(h.key)
        ) {
          needS16DoubleLines.push(i + 1);
        } else needLines.push(i + 1);

        if (h.key === "currentAmount") {
          if (h.value === "Additions") {
            totalContent.push(formatAmount(addition[h.key]));
          } else {
            totalContent.push(
              formatAmount(
                disposal[h.key] + (addition["currentYearDisposal"] || 0)
              )
            );
          }
        } else {
          totalContent.push(
            formatAmount(
              Math.round(addition[h.key]) + Math.round(disposal[h.key])
            )
          );
        }
      } else if (["principalPaid"].includes(h.key)) {
        if (totalIndex !== stopIndex) {
          const letter = String.fromCharCode(97 + totalIndex);
          totalContent.push(boldAutoColumn(`Sub total (${letter})`));
          needAdd = true;
        } else {
          totalContent.push(boldAutoColumn("Total (c) = (a) + (b)"));
        }
      } else {
        totalContent.push("");
      }
    });

    contentData.push(totalContent);
    ignoreLines.push(contentData.length - 1);
    totalLines.push(contentData.length - 2, contentData.length - 1);
    totalDoubleLines.push(contentData.length - 1);

    if (caType === "S16") {
      let disregardContent = [""];
      let totalDisregardContent = [""];
      headers.forEach((h, i) => {
        if (["initialAllowance", "annualAllowance"].includes(h.key)) {
          let ori_total = (addition?.[h.key] || 0) + (disposal?.[h.key] || 0);

          let total =
            (addition?.disregard?.[h.key] || 0) +
            (disposal?.disregard?.[h.key] || 0);
          disregardContent.push(formatAmount(Math.abs(total)));
          totalDisregardContent.push(formatAmount(ori_total - total));
        } else if (h.key === "disregardRate") {
          disregardContent.push(boldAutoColumn("Disregarded"));
          totalDisregardContent.push("");
        } else if (
          ["Additions", "Prior Year Disposal"].includes(h.value) &&
          caType === "S16"
        ) {
          if (!hasHp || (hasHp && totalIndex === stopIndex)) {
            disregardContent.push(
              getRef(
                {
                  currentAmount: {
                    Additions: "FAAdditions",
                    "Prior Year Disposal": "FADisposals",
                  },
                },
                h.key,
                h.value
              )
            );
            totalDisregardContent.push("");
          } else {
            disregardContent.push("");
            totalDisregardContent.push("");
          }
        } else {
          disregardContent.push("");
          totalDisregardContent.push("");
        }
      });

      contentData.push(disregardContent);
      contentData.push(totalDisregardContent);
      totalDisregardLines.push(contentData.length - 2, contentData.length - 1);
      totalDisregardDoubleLines.push(contentData.length - 1);
      needAdd = true;
    }
    if (needAdd) totalIndex++;
  };

  // Process Disposal Details
  if (disposalDetails?.length) {
    disposalDetails.forEach((row) => processRow(row, "disposal"));
  }

  // Process Additions Details
  if (additionsDetails?.length) {
    additionsDetails.forEach((row) => processRow(row, "addition"));
  }

  if (hasAllowance) processTotal(totalAdditionsDetails, totalDisposalDetails);

  if (hasHp) contentData.push([boldAutoColumn("Hire Purchase")]);

  if (hirePurchaseDisposal?.length) {
    hirePurchaseDisposal.forEach((row) => processRow(row, "disposal"));
  }

  if (hirePurchaseAdditions?.length) {
    hirePurchaseAdditions.forEach((row) => processRow(row, "addition"));
  }

  if (hasHp) processTotal(totalHpAdditionsDetails, totalHpDisposalDetails);

  if (hasAllowance && hasHp) {
    contentData.push([]);

    let sumAdditions = sumObjects(
      totalAdditionsDetails,
      totalHpAdditionsDetails
    );
    let sumDisposals = sumObjects(totalDisposalDetails, totalHpDisposalDetails);

    if (caType === "S16") {
      sumAdditions.disregard = sumObjects(
        totalAdditionsDetails.disregard,
        totalHpAdditionsDetails.disregard
      );
      sumDisposals.disregard = sumObjects(
        totalDisposalDetails.disregard,
        totalHpDisposalDetails.disregard
      );
    }

    processTotal(sumAdditions, sumDisposals);
  }

  if (contentData.length > 0) {
    const keyMap =
      caType === "S16"
        ? {
            initialAllowance: "CASummary",
            annualAllowance: "CASummary",
          }
        : {
            currentAmount: {
              Additions: "FAAdditions",
              "Prior Year Disposal": "FADisposals",
            },
            initialAllowance: "CASummary",
            annualAllowance: "CASummary",
          };

    let reference = [""];

    headers.forEach((h) => {
      reference.push(getRef(keyMap, h.key, h.value));
    });

    contentData.push(reference);
  }

  let numericCellWidth = 22;
  let tableFontSize = 8;

  if (caType === "S16") {
    tableFontSize = 7;
    numericCellWidth = 19;
  }

  let columnStyles = {
    0: { halign: "left" },
    ...headers.reduce((acc, h, i) => {
      if (
        [
          "initialAllowance",
          "annualAllowance",
          "twdvBf",
          "twdvCf",
          "currentAmount",
          "totalAmount",
        ].includes(h.key)
      )
        acc[i + 1] = {
          halign: h.align || "right",
          cellWidth: numericCellWidth,
        };
      else if (["principalPaid"].includes(h.key)) {
        acc[i + 1] = {
          halign: h.align || "right",
        };
      } else acc[i + 1] = { halign: h.align || "left" };
      return acc;
    }, {}),
  };

  const disregardLine = hasHp ? [12, 13] : [11, 12];

  if (caType === "S16") {
    pdf.fs = 9;
  }
  if (contentData.length > 0)
    buildAutoTable({
      pdf,
      head: [
        [
          "YA",
          ...headers.map((item) => {
            if (item.value === "Prior Year Disposal") return "Disposal";

            if (item.value === "Initial Allowance (IA)")
              return "Initial\nAllowance (IA)";

            if (item.value === "Annual Allowance (AA)")
              return "Annual\nAllowance (AA)";

            return item.value;
          }),
        ],
      ],
      body: contentData,
      schedule,
      company,
      columnStyles: columnStyles,
      didDrawCell: function (data) {
        let shouldIgnore =
          caType === "S16" &&
          disregardLine.includes(data.column.index) &&
          ignoreLines.includes(data.row.index);
        beautyAutoLine(
          data,
          pdf.doc,
          (totalLines.includes(data.row.index) &&
            (needLines.includes(data.column.index) ||
              needS16DoubleLines.includes(data.column.index))) ||
            (totalDisregardLines.includes(data.row.index) &&
              needS16DoubleLines.includes(data.column.index)),
          (totalDoubleLines.includes(data.row.index) &&
            needLines.includes(data.column.index)) ||
            (totalDisregardDoubleLines.includes(data.row.index) &&
              needS16DoubleLines.includes(data.column.index)),
          null,
          shouldIgnore
        );
      },
      lineSettings: {
        totalLines: caType === "S16" ? [totalLines[0]] : totalLines,
        subTotalLines: totalDisregardLines,
        subTotalDoubleLines: totalDisregardDoubleLines,
        subTotalRows: needS16DoubleLines,
        totalDoubleLines,
        totalRows: needLines.concat(needS16DoubleLines),
        totalDoubleRows: needLines,
      },
    });

  if (caSummaryDetails && caSummaryDetails.length > 0 && forView) {
    const summaryHeaders = ["CA Summary"];
    const includeIA = (
      headerSettings.find((h) => h.key === "initialAllowance")?.visibility || []
    ).includes(caType);

    if (includeIA) summaryHeaders.push("Initial Allowance");

    summaryHeaders.push("Annual Allowance");

    const summaryBody = caSummaryDetails.map((row) => {
      if (includeIA)
        return [
          row.caSummary,
          formatAmount(row.totalIA),
          formatAmount(row.totalAA),
        ];
      else return [row.caSummary, formatAmount(row.totalAA)];
    });

    let totalLines = [];
    let totalDoubleLines = [];
    if (summaryBody.length > 0) {
      let totalRow = [
        {
          content: "Total",
          colSpan: includeIA ? 0 : 1,
          styles: { halign: "right", fontStyle: "bold" },
        },
      ];

      if (includeIA) {
        totalRow.push(
          boldAutoColumn(
            formatAmount(
              caSummaryDetails.reduce((acc, row) => acc + row.totalIA, 0)
            )
          )
        );
      }

      totalRow.push(
        boldAutoColumn(
          formatAmount(
            caSummaryDetails.reduce((acc, row) => acc + row.totalAA, 0)
          )
        )
      );

      totalLines.push(summaryBody.length - 1);
      summaryBody.push(totalRow);
      totalLines.push(summaryBody.length - 1);
      totalDoubleLines.push(summaryBody.length - 1);

      pdf.fs = 10;
      buildAutoTable({
        pdf,
        customStartY: pdf.startY + 5,
        checkHeaderCondition: true,
        head: [summaryHeaders],
        body: summaryBody,
        schedule,
        company,
        columnStyles: {
          0: {
            halign: "left",
            minCellWidth: 50,
          },
          1: {
            halign: "right",
          },
          2: {
            halign: "right",
          },
        },
        didParseCell: function (data) {
          if (data.row.index === 0 && [0].includes(data.column.index)) {
            data.cell.styles.halign = "left";
          }
          if (data.row.index === 0 && [1, 2].includes(data.column.index)) {
            data.cell.styles.halign = "right";
          }
        },
        didDrawCell: function (data) {
          beautyAutoLine(
            data,
            pdf.doc,
            totalLines.includes(data.row.index) &&
              [1, 2].includes(data.column.index),
            totalDoubleLines.includes(data.row.index) &&
              [1, 2].includes(data.column.index)
          );
        },
        lineSettings: {
          totalLines,
          totalDoubleLines,
          totalRows: [1, 2],
        },
      });
    }
  }

  // Check if any row in hirePurchaseDisposal has ori_twdvBf !== twdvBf, and if so, add review note
  if (hirePurchaseDisposal && Array.isArray(hirePurchaseDisposal)) {
    let isFirst = true;
    hirePurchaseDisposal.forEach((row) => {
      if (
        typeof row.ori_twdvBf !== "undefined" &&
        typeof row.twdvBf !== "undefined" &&
        row.ori_twdvBf !== row.twdvBf
      ) {
        if (isFirst) {
          isFirst = false;
          if (reviewNotes.trim() !== "") {
            reviewNotes += "\n\n";
          }
          reviewNotes += "[Modified Hire Purchase TWDV b/f Rows]\n";
          isFirst = false;
        }
        const msg = `YA: ${row.ya} | CA Name: ${
          row.caName
        } | Original TWDV b/f: ${formatAmount(
          row.ori_twdvBf
        )} | Updated TWDV b/f: ${formatAmount(row.twdvBf)};\n`;
        reviewNotes += msg;
      }

      let firstManualData = true;

      if (row.manualData) {
        if (reviewNotes.trim() !== "") {
          reviewNotes += "\n\n";
        }

        if (firstManualData) {
          reviewNotes +=
            "The row marked YA (*) was manually added and not generated from the HP schedules:\n";
          firstManualData = false;
        }
        reviewNotes += `- YA: ${row.ya}, CA Name: ${row.caName}\n`;
      }
    });
  }

  reviewNotes = appendFaRegisterImportReviewNote(reviewNotes, [
    ...(additionsDetails || []),
    ...(disposalDetails || []),
    ...(hirePurchaseAdditions || []),
    ...(hirePurchaseDisposal || []),
  ]);

  // Build amount calculation notes based on CA Summary
  let builtInNotes = [];

  if (caSummaryDetails && caSummaryDetails.length > 0) {
    const includeIA = headerSettings.some(
      (h) => h.key === "initialAllowance" && h.visibility.includes(caType)
    );

    // Collect all rows from additions, disposals, HP additions, and HP disposals
    const allRows = [
      ...(additionsDetails || []),
      ...(disposalDetails || []),
      ...(hirePurchaseAdditions || []),
      ...(hirePurchaseDisposal || []),
    ];

    // Helper function to build notes for a CA Summary item
    function buildCaSummaryNotes(caSummaryItem, fieldKey, fieldLabel) {
      const amounts = [];
      const caSummaryName = caSummaryItem.caSummary;

      // Filter rows that match this CA Summary
      const matchingRows = allRows.filter((row) => {
        const rowCaSummary = getRowCaSummaryKeyFromRow(row);
        return rowCaSummary && rowCaSummary === caSummaryName;
      });

      if (matchingRows.length === 0) return [];

      matchingRows.forEach((row) => {
        // Skip deferred rows for initialAllowance and annualAllowance
        if (
          ["initialAllowance", "annualAllowance"].includes(fieldKey) &&
          row.isDeferred
        ) {
          return; // Skip this row
        }

        // Map fieldKey to row field name
        const rowFieldKey =
          fieldKey === "totalIA" ? "initialAllowance" : "annualAllowance";

        // Get base value
        let baseValue = row[rowFieldKey] || 0;

        // Add base value if non-zero
        if (baseValue !== 0) {
          amounts.push(formatAmount(baseValue));
        }

        // Get enhanced value if exists
        if (row.enhanced && row.allowanceType === "Enhanced") {
          const enhancedValue = row.enhanced[rowFieldKey] || 0;

          // Add enhanced value if non-zero
          if (enhancedValue !== 0) {
            amounts.push(formatAmount(enhancedValue));
          }
        }

        // Subtract disregard if exists (matching generateCaSummary logic)
        if (row.isDisregard && row.disregard && row.disregard[rowFieldKey]) {
          const disregardValue = row.disregard[rowFieldKey] || 0;
          if (disregardValue !== 0) {
            amounts.push(formatAmount(Math.abs(disregardValue)));
          }
        }
      });

      if (amounts.length === 0) return [];

      // Get total from caSummaryDetails
      const totalFromSummary = caSummaryItem[fieldKey] || 0;

      return [
        `${caSummaryName} - ${fieldLabel}: ${amounts.join(
          " + "
        )} = ${formatAmount(totalFromSummary)}`,
      ];
    }

    // Build notes for each CA Summary item
    caSummaryDetails.forEach((caSummaryItem) => {
      // Build notes for Initial Allowance if applicable
      if (includeIA) {
        const iaNotes = buildCaSummaryNotes(
          caSummaryItem,
          "totalIA",
          "Initial Allowance"
        );
        if (iaNotes.length > 0) builtInNotes.push(...iaNotes);
      }

      // Build notes for Annual Allowance
      const aaNotes = buildCaSummaryNotes(
        caSummaryItem,
        "totalAA",
        "Annual Allowance"
      );
      if (aaNotes.length > 0) builtInNotes.push(...aaNotes);
    });
  }

  if (builtInNotes.length > 0) {
    if (reviewNotes !== "") reviewNotes += "\n\n";
    reviewNotes += "[Amount Calculation]\n";
    reviewNotes += builtInNotes.join("\n");
  }

  buildAutoNote(
    pdf,
    forView,
    company,
    schedule,
    notes,
    reviewNotes,
    [],
    footerSettings
  );

  return pdf.getDoc(forView, company, schedule);
};

export const generateS19SchedulePdf = (
  forView,
  schedule,
  company,
  caType,
  refSources,
  additionsDetails,
  disposalDetails,
  hirePurchaseAdditions,
  hirePurchaseDisposal,
  totalAdditionsDetails,
  totalDisposalDetails,
  totalHpAdditionsDetails,
  totalHpDisposalDetails,
  totalNetAA,
  caSummaryDetails,
  notes,
  reviewNotes,
  isExcel = false
) => {
  const keyMap = {
    currentAmount: {
      Additions: "FAAdditions",
      "Prior Year Disposal": "FADisposals",
    },
    initialAllowance: "CASummary",
    annualAllowance: "CASummary",
  };

  const pdf = new BasePdfGenerator(true, isExcel);

  const scheduleCaType = "S19";

  const hasAllowance = disposalDetails?.length || additionsDetails?.length;
  const hasHp =
    (hirePurchaseAdditions && hirePurchaseAdditions.length > 0) ||
    (hirePurchaseDisposal && hirePurchaseDisposal.length > 0);
  const getRef = (keyMap, key, value) => {
    const refKey =
      typeof keyMap[key] === "string" ? keyMap[key] : keyMap[key]?.[value];

    return refKey && refSources[refKey]
      ? lineBreakText(`Sch ${refSources[refKey]}`, {
          top: 3,
          bottom: 0,
          right: 1,
        })
      : "";
  };

  const splitHpTables = hasHp && hasAllowance;

  const isHeaderVisible = (item, isHpSection) => {
    if (!item.visibility.includes(scheduleCaType)) return false;
    if (isHpSection) {
      if (item.showInHirePurchase === false) return false;
      return true;
    }
    if (item.showInHirePurchase === true) return false;
    return true;
  };

  const getHeadersForSection = (isHpSection) =>
    headerSettings.filter((item) => {
      if (!item.value) return false;
      if (!isHeaderVisible(item, isHpSection)) return false;
      if (item.value === "Current Year Disposal") return false;

      if (item.value === "Principal Paid") {
        if (!hasHp) return false;
        if (splitHpTables && !isHpSection) return false;
        return true;
      }

      if (item.value === "Deferred" || item.value === "Disregard") return false;

      return true;
    });

  const buildCaScheduleMainTable = ({
    mode,
    customStartY,
    addPlainTitle,
    tableTitle,
  }) => {
    const isHpSection =
      mode === "hpOnly" || (mode === "full" && hasHp && !hasAllowance);

    const headers = getHeadersForSection(isHpSection);

    const includeNonHp = mode === "full" || mode === "nonHpOnly";
    const includeHpRows = mode === "hpOnly" || (mode === "full" && hasHp);
    const includeHpBannerRow = mode === "full" && hasHp;
    const includeGrandCombined = mode === "full" && hasAllowance && hasHp;

    let totalLines = [];
    let totalDoubleLines = [];

    let totalDisregardLines = [];
    let totalDisregardDoubleLines = [];

    let needLines = [];
    let needS16DoubleLines = [];
    let ignoreLines = [];

    let contentData = [];

    const processRow = (row, type) => {
      let content = [];

      content.push(getCaSchedulePdfYa(row));

      let hasEnhanced = false;
      headers.forEach((h, i) => {
        if (
          [
            "initialAllowance",
            "annualAllowance",
            "twdvBf",
            "twdvCf",
            "currentAmount",
            "totalAmount",
            "principalPaid",
          ].includes(h.key)
        ) {
          if (["initialAllowance", "annualAllowance"].includes(h.key)) {
            content.push(
              row.isDeferred &&
              !(
                row.isHirePurchase &&
                !row.manualData &&
                row.caName === "S19" &&
                h.key === "initialAllowance"
              )
                ? "Deferred"
                : formatAmount(row[h.key])
            );
          } else if (h.key === "currentAmount") {
            if (type === "disposal") {
              if (h.value === "Additions") content.push("0");
              else content.push(formatAmount(row[h.key]));
            } else {
              if (h.value === "Additions")
                content.push(formatAmount(row[h.key]));
              else content.push(formatAmount(row["currentYearDisposal"]));
            }
          } else if (h.key === "principalPaid") {
            if (!row[h.key]) content.push("");
            else content.push(formatAmount(row[h.key]));
          } else {
            content.push(formatAmount(row[h.key]));
          }
        } else if (["initialAllowanceRate"].includes(h.key)) {
          content.push(`${row[h.key]}` || "0");
        } else if (["isDeferred", "isDisregard"].includes(h.key)) {
          content.push(row[h.key] ? "Yes" : "No");
        } else if (["allowanceType"].includes(h.key)) {
          if (row["allowanceType"] === "Enhanced") {
            hasEnhanced = true;
            content.push("Base");
          } else content.push(`${row[h.key]}`);
        } else if (["caSummary"].includes(h.key)) {
          if (
            !["S16 - (LIA / IBA)", "S16 - LIA", "S16 - IBA"].includes(
              row["caName"]
            )
          )
            content.push(row.caSummary);
          else if (["S16 - LIA", "S16 - IBA"].includes(row["caName"]))
            content.push(row.caName);
          else content.push(row.s16Type);
        } else if (["caName"].includes(h.key)) {
          if (["S16 - LIA", "S16 - IBA"].includes(row["caName"]))
            content.push("S16 - (LIA / IBA)");
          else content.push(row.caName);
        } else {
          content.push(`${row[h.key]}`);
        }
      });

      contentData.push(content);

      if (hasEnhanced) {
        content = [getCaSchedulePdfYa(row)];
        headers.forEach((h, i) => {
          if (
            [
              "initialAllowance",
              "annualAllowance",
              "twdvBf",
              "twdvCf",
              "currentAmount",
              "totalAmount",
              "principalPaid",
            ].includes(h.key)
          ) {
            if (["initialAllowance", "annualAllowance"].includes(h.key)) {
              content.push(
                row.isDeferred ? "Deferred" : formatAmount(row.enhanced[h.key])
              );
            } else if (h.key === "currentAmount") {
              if (type === "disposal") {
                if (h.value === "Additions") content.push("0");
                else content.push(formatAmount(row.enhanced[h.key]));
              } else {
                if (h.value === "Additions")
                  content.push(formatAmount(row.enhanced[h.key]));
                else
                  content.push(
                    formatAmount(row.enhanced["currentYearDisposal"])
                  );
              }
            } else if (h.key === "twdvBf") {
              content.push(formatAmount(row.enhanced[h.key]));
            } else if (h.key === "principalPaid") {
              if (!row.enhanced[h.key]) content.push("");
              else content.push(formatAmount(row.enhanced[h.key]));
            } else {
              content.push(formatAmount(row.enhanced[h.key]));
            }
          } else if (["isDeferred", "isDisregard"].includes(h.key)) {
            content.push(row[h.key] ? "Yes" : "No");
          } else if (["caSummary"].includes(h.key)) {
            if (["S16 - LIA", "S16 - IBA"].includes(row["caName"]))
              content.push(row.caName);
            else content.push(row.s16Type);
          } else if (["caName"].includes(h.key)) {
            if (["S16 - LIA", "S16 - IBA"].includes(row["caName"]))
              content.push("S16 - (LIA / IBA)");
            else content.push(row.caName);
          } else {
            content.push(`${row[h.key]}`);
          }
        });

        contentData.push(content);
      }
    };

    let totalIndex = 0;
    const stopIndex = 2;
    const processTotal = (addition, disposal, isHirePurchase = false) => {
      let totalContent = [""];

      let needAdd = false;
      headers.forEach((h, i) => {
        if (
          [
            "initialAllowance",
            "annualAllowance",
            "twdvBf",
            "twdvCf",
            "currentAmount",
            "totalAmount",
          ].includes(h.key)
        ) {
          needLines.push(i + 1);

          if (h.key === "currentAmount") {
            if (h.value === "Additions") {
              totalContent.push(formatAmount(addition[h.key]));
            } else {
              totalContent.push(
                formatAmount(
                  disposal[h.key] + (addition["currentYearDisposal"] || 0)
                )
              );
            }
          } else {
            totalContent.push(
              formatAmount(
                Math.round(addition[h.key]) + Math.round(disposal[h.key])
              )
            );
          }
        } else if (
          (h.key === "noOfYearClaimsBf" && !isHirePurchase) ||
          (h.key === "principalPaid" && isHirePurchase)
        ) {
          if (totalIndex !== stopIndex) {
            totalContent.push(boldAutoColumn(`Sub total`));
            needAdd = true;
          } else {
            totalContent.push(boldAutoColumn("Total (c) = (a) + (b)"));
          }
        } else {
          totalContent.push("");
        }
      });

      contentData.push(totalContent);
      ignoreLines.push(contentData.length - 1);
      totalLines.push(contentData.length - 2, contentData.length - 1);
      totalDoubleLines.push(contentData.length - 1);

      if (needAdd) totalIndex++;
    };

    // Process Disposal Details
    if (includeNonHp && disposalDetails?.length) {
      disposalDetails.forEach((row) => processRow(row, "disposal"));
    }

    // Process Additions Details
    if (includeNonHp && additionsDetails?.length) {
      additionsDetails.forEach((row) => processRow(row, "addition"));
    }

    if (includeNonHp && hasAllowance)
      processTotal(totalAdditionsDetails, totalDisposalDetails);

    if (includeHpBannerRow && hasHp)
      contentData.push([boldAutoColumn("Hire Purchase")]);

    if (includeHpRows && hirePurchaseDisposal?.length) {
      hirePurchaseDisposal.forEach((row) => processRow(row, "disposal"));
    }

    if (includeHpRows && hirePurchaseAdditions?.length) {
      hirePurchaseAdditions.forEach((row) => processRow(row, "addition"));
    }

    if (includeHpRows)
      processTotal(totalHpAdditionsDetails, totalHpDisposalDetails, true);

    if (includeGrandCombined) {
      contentData.push([]);

      let sumAdditions = sumObjects(
        totalAdditionsDetails,
        totalHpAdditionsDetails
      );
      let sumDisposals = sumObjects(
        totalDisposalDetails,
        totalHpDisposalDetails
      );

      processTotal(sumAdditions, sumDisposals);
    }

    if (contentData.length > 0) {
      let reference = [""];

      headers.forEach((h) => {
        reference.push(getRef(keyMap, h.key, h.value));
      });

      contentData.push(reference);
    }

    let numericCellWidth = 22;

    let columnStyles = {
      0: { halign: "left" },
      ...headers.reduce((acc, h, i) => {
        if (
          [
            "initialAllowance",
            "annualAllowance",
            "twdvBf",
            "twdvCf",
            "currentAmount",
            "totalAmount",
          ].includes(h.key)
        )
          acc[i + 1] = {
            halign: h.align || "right",
            cellWidth: numericCellWidth,
          };
        else if (["principalPaid"].includes(h.key)) {
          acc[i + 1] = {
            halign: h.align || "right",
          };
        } else acc[i + 1] = { halign: h.align || "left" };
        return acc;
      }, {}),
    };

    if (contentData.length > 0) {
      const tableExtras = {};
      if (typeof customStartY === "number") {
        tableExtras.customStartY = customStartY;
      }
      if (addPlainTitle && tableTitle) {
        tableExtras.addPlainTitle = true;
        tableExtras.tableTitle = tableTitle;
      }
      buildAutoTable({
        pdf,
        head: [
          [
            "YA",
            ...headers.map((item) => {
              if (item.value === "Prior Year Disposal") return "Disposal";

              if (item.value === "Initial Allowance (IA)")
                return "Initial\nAllowance (IA)";

              if (item.value === "Annual Allowance (AA)")
                return "Annual\nAllowance (AA)";

              return item.value;
            }),
          ],
        ],
        body: contentData,
        schedule,
        company,
        columnStyles: columnStyles,
        didDrawCell: function (data) {
          beautyAutoLine(
            data,
            pdf.doc,
            totalLines.includes(data.row.index) &&
              needLines.includes(data.column.index),
            totalDoubleLines.includes(data.row.index) &&
              needLines.includes(data.column.index),
            null,
            false
          );
        },
        lineSettings: {
          totalLines,
          subTotalLines: totalDisregardLines,
          subTotalDoubleLines: totalDisregardDoubleLines,
          subTotalRows: needS16DoubleLines,
          totalDoubleLines,
          totalRows: needLines,
          totalDoubleRows: needLines,
        },
        ...tableExtras,
      });
    }
  };

  if (splitHpTables) {
    buildCaScheduleMainTable({ mode: "nonHpOnly" });
    buildCaScheduleMainTable({
      mode: "hpOnly",
      customStartY:
        !isExcel && pdf.doc.lastAutoTable
          ? pdf.doc.lastAutoTable.finalY + 10
          : undefined,
      addPlainTitle: true,
      tableTitle: "Hire Purchase",
    });
  } else {
    buildCaScheduleMainTable({ mode: "full" });
  }

  if (caSummaryDetails && caSummaryDetails.length > 0 && forView) {
    const summaryHeaders = [
      "CA Summary",
      "Initial Allowance",
      "Annual Allowance",
    ];

    const numericColIndices = Array.from(
      { length: summaryHeaders.length - 1 },
      (_, i) => i + 1
    );

    const summaryBody = caSummaryDetails.map((row) => {
      const cells = [
        row.caSummary,
        formatAmount(row.totalIA || 0),
        formatAmount(row.totalAA),
      ];
      return cells;
    });

    let totalLines = [];
    let totalDoubleLines = [];
    if (summaryBody.length > 0) {
      const totalRow = [
        "",
        getRef(keyMap, "initialAllowance", "Initial Allowance (IA)"),
        getRef(keyMap, "annualAllowance", "Annual Allowance (AA)"),
      ];

      summaryBody.push(totalRow);
      totalLines.push(summaryBody.length - 2);
      totalDoubleLines.push(summaryBody.length - 2);

      const columnStyles = {
        0: {
          halign: "left",
          minCellWidth: 44,
        },
      };
      summaryHeaders.forEach((_, idx) => {
        if (idx === 0) return;
        columnStyles[idx] = {
          halign: "right",
          cellWidth: 36,
        };
      });

      pdf.fs = 10;
      buildAutoTable({
        pdf,
        customStartY: pdf.startY + 5,
        checkHeaderCondition: true,
        head: [summaryHeaders],
        body: summaryBody,
        schedule,
        company,
        columnStyles,
        didParseCell: function (data) {
          if (data.row.index === 0 && data.column.index === 0) {
            data.cell.styles.halign = "left";
          }
          if (data.row.index === 0 && data.column.index > 0) {
            data.cell.styles.halign = "right";
          }
        },
        didDrawCell: function (data) {
          beautyAutoLine(
            data,
            pdf.doc,
            totalLines.includes(data.row.index) &&
              numericColIndices.includes(data.column.index),
            totalDoubleLines.includes(data.row.index) &&
              numericColIndices.includes(data.column.index)
          );
        },
        lineSettings: {
          totalLines,
          totalDoubleLines,
          totalRows: numericColIndices,
        },
      });
    }
  }

  // Check if any row in hirePurchaseDisposal has ori_twdvBf !== twdvBf, and if so, add review note
  if (hirePurchaseDisposal && Array.isArray(hirePurchaseDisposal)) {
    let isFirst = true;
    hirePurchaseDisposal.forEach((row) => {
      if (
        typeof row.ori_twdvBf !== "undefined" &&
        typeof row.twdvBf !== "undefined" &&
        row.ori_twdvBf !== row.twdvBf
      ) {
        if (isFirst) {
          isFirst = false;
          if (reviewNotes.trim() !== "") {
            reviewNotes += "\n\n";
          }
          reviewNotes += "[Modified Hire Purchase TWDV b/f Rows]\n";
          isFirst = false;
        }
        const msg = `YA: ${row.ya} | CA Name: ${
          row.caName
        } | Original TWDV b/f: ${formatAmount(
          row.ori_twdvBf
        )} | Updated TWDV b/f: ${formatAmount(row.twdvBf)};\n`;
        reviewNotes += msg;
      }

      let firstManualData = true;

      if (row.manualData) {
        if (reviewNotes.trim() !== "") {
          reviewNotes += "\n\n";
        }

        if (firstManualData) {
          reviewNotes +=
            "The row marked YA (*) was manually added and not generated from the HP schedules:\n";
          firstManualData = false;
        }
        reviewNotes += `- YA: ${row.ya}, CA Name: ${row.caName}\n`;
      }
    });
  }

  reviewNotes = appendFaRegisterImportReviewNote(reviewNotes, [
    ...(additionsDetails || []),
    ...(disposalDetails || []),
    ...(hirePurchaseAdditions || []),
    ...(hirePurchaseDisposal || []),
  ]);

  // Build amount calculation notes based on CA Summary
  let builtInNotes = [];

  if (caSummaryDetails && caSummaryDetails.length > 0) {
    const includeIA = headerSettings.some(
      (h) => h.key === "initialAllowance" && h.visibility.includes(caType)
    );

    // Collect all rows from additions, disposals, HP additions, and HP disposals
    const allRows = [
      ...(additionsDetails || []),
      ...(disposalDetails || []),
      ...(hirePurchaseAdditions || []),
      ...(hirePurchaseDisposal || []),
    ];

    // Helper function to build notes for a CA Summary item
    function buildCaSummaryNotes(caSummaryItem, fieldKey, fieldLabel) {
      const amounts = [];
      const caSummaryName = caSummaryItem.caSummary;

      // Filter rows that match this CA Summary
      const matchingRows = allRows.filter((row) => {
        const rowCaSummary = getRowCaSummaryKeyFromRow(row);
        return rowCaSummary && rowCaSummary === caSummaryName;
      });

      if (matchingRows.length === 0) return [];

      matchingRows.forEach((row) => {
        // Skip deferred rows for initialAllowance and annualAllowance
        if (
          ["initialAllowance", "annualAllowance"].includes(fieldKey) &&
          row.isDeferred
        ) {
          return; // Skip this row
        }

        // Map fieldKey to row field name
        const rowFieldKey =
          fieldKey === "totalIA" ? "initialAllowance" : "annualAllowance";

        // Get base value
        let baseValue = row[rowFieldKey] || 0;

        // Add base value if non-zero
        if (baseValue !== 0) {
          amounts.push(formatAmount(baseValue));
        }

        // Get enhanced value if exists
        if (row.enhanced && row.allowanceType === "Enhanced") {
          const enhancedValue = row.enhanced[rowFieldKey] || 0;

          // Add enhanced value if non-zero
          if (enhancedValue !== 0) {
            amounts.push(formatAmount(enhancedValue));
          }
        }

        // Subtract disregard if exists (matching generateCaSummary logic)
        if (row.isDisregard && row.disregard && row.disregard[rowFieldKey]) {
          const disregardValue = row.disregard[rowFieldKey] || 0;
          if (disregardValue !== 0) {
            amounts.push(formatAmount(Math.abs(disregardValue)));
          }
        }
      });

      if (amounts.length === 0) return [];

      // Get total from caSummaryDetails
      const totalFromSummary = caSummaryItem[fieldKey] || 0;

      return [
        `${caSummaryName} - ${fieldLabel}: ${amounts.join(
          " + "
        )} = ${formatAmount(totalFromSummary)}`,
      ];
    }

    // Build notes for each CA Summary item
    caSummaryDetails.forEach((caSummaryItem) => {
      // Build notes for Initial Allowance if applicable
      if (includeIA) {
        const iaNotes = buildCaSummaryNotes(
          caSummaryItem,
          "totalIA",
          "Initial Allowance"
        );
        if (iaNotes.length > 0) builtInNotes.push(...iaNotes);
      }

      // Build notes for Annual Allowance
      const aaNotes = buildCaSummaryNotes(
        caSummaryItem,
        "totalAA",
        "Annual Allowance"
      );
      if (aaNotes.length > 0) builtInNotes.push(...aaNotes);
    });
  }

  if (builtInNotes.length > 0) {
    if (reviewNotes !== "") reviewNotes += "\n\n";
    reviewNotes += "[Amount Calculation]\n";
    reviewNotes += builtInNotes.join("\n");
  }

  buildAutoNote(
    pdf,
    forView,
    company,
    schedule,
    notes,
    reviewNotes,
    [],
    footerSettings
  );

  return pdf.getDoc(forView, company, schedule);
};

export const generateCaSummaryPdf = (
  forView,
  schedule,
  company,
  summary,
  netTotal,
  notes,
  reviewNotes,
  isExcel = false
) => {
  const pdf = new BasePdfGenerator(false, isExcel);

  let contentColumns = ["", "", ""];
  let totalLines = [];
  let totalDoubleLines = [];

  let contentData = [];
  let labelRow = ["", "$", "$"];
  contentData.push(labelRow);

  if (summary) {
    summary.map((row) => {
      contentData.push([boldAutoColumn(row.caSummary)]);

      if (row.initialAllowance !== undefined && row.initialAllowance !== 0) {
        contentData.push([
          "Initial Allowance",
          formatAmount(row.initialAllowance),
        ]);
      }

      if (row.annualAllowance !== undefined && row.annualAllowance !== 0) {
        contentData.push([
          "Annual Allowance",
          formatAmount(row.annualAllowance),
        ]);
      }

      if (row.bcBa !== undefined && row.bcBa !== 0) {
        contentData.push(["BA / (BC)", formatAmount(row.bcBa)]);
      }

      totalLines.push(contentData.length - 1);

      contentData.push(["", "", formatAmount(row.total)]);
    });

    contentData.push(["", "", formatAmount(netTotal)]);

    let finalLine = [];
    finalLine.push(contentData.length - 2);
    finalLine.push(contentData.length - 1);
    totalDoubleLines.push(contentData.length - 1);

    if (forView)
      contentData.push([
        "",
        "",
        autoTextColor("Deduct / Main Comp", [0, 0, 0]),
      ]);

    if (contentData.length > 0)
      buildAutoTable({
        pdf,
        head: [contentColumns],
        body: contentData,
        customStartY: 55,
        schedule,
        company,
        columnStyles: {
          0: { halign: "left", minCellWidth: 60 },
          1: { halign: "right", cellWidth: 25 },
          2: { halign: "right", cellWidth: 25 },
        },
        didParseCell: function (data) {
          if (data.row.index === 0 && [1, 2].includes(data.column.index)) {
            data.cell.styles.halign = "center";
          }
        },
        didDrawCell: function (data) {
          beautyAutoLine(
            data,
            pdf.doc,
            (data.row.index === 0 && [1, 2].includes(data.column.index)) ||
              (totalLines.includes(data.row.index) &&
                [1].includes(data.column.index)) ||
              (finalLine.includes(data.row.index) &&
                [2].includes(data.column.index)),
            totalDoubleLines.includes(data.row.index) &&
              [2].includes(data.column.index)
          );
        },
        lineSettings: {
          totalLines: finalLine,
          subTotalLines: totalLines,
          totalDoubleLines,
          subTotalRows: [1],
          totalRows: [2],
          labelRows: [1, 2],
        },
      });
  }

  // pdf.buildNotes(notes, reviewNotes, forView, [], company);

  return pdf.getDoc(forView, company, schedule);
};

export const generateHirePurchasePdf = (
  forView,
  schedule,
  company,
  assets,
  assetCodeList,
  summaryData,
  caTypeTotals,
  notes,
  reviewNotes,
  isExcel = false,
  showDecimals = false
) => {
  const pdf = new BasePdfGenerator(true, isExcel);

  // --- Asset Info Card Format ---
  if (assets.length > 0) {
    assets.forEach((asset, idx) => {
      let assetInfo = [
        boldAutoColumn("CA Type:"),
        asset.caType || "",
        boldAutoColumn("No. of Years of Claim:"),
        asset.noOfYears || "",
        boldAutoColumn("Cost:"),
        formatAmount(asset.cost),
      ];

      if (asset.initialAllowanceRate) {
        assetInfo.push(
          boldAutoColumn("Initial Allowance:"),
          asset.initialAllowanceRate !== undefined &&
            asset.initialAllowanceRate !== null &&
            asset.initialAllowanceRate !== ""
            ? asset.initialAllowanceRate + "%"
            : "-"
        );
      }

      const cost = parseFloat(asset.cost) || 0;
      const principalPaid = asset.principalPaid || [];
      const principalDetails = asset.principalDetails || {};
      const isS19Asset = asset.caType === "S19";
      const shouldShowDecimals = false;
      // Get all years from principalDetails
      const allPrincipalYears = Object.keys(principalDetails)
        .map(Number)
        .filter(
          (year) =>
            !(
              isS19Asset === true &&
              asset?.isCurrentYearDisposed === true &&
              year > company.yearOfAssessment
            )
        )
        .sort((a, b) => a - b);

      // Calculate totals
      const totalPrincipalPaid = calculateTotalPrincipalPaid(principalPaid);

      let contentData = [assetInfo];

      // Optional warning row if cost mismatch
      if (forView && cost && totalPrincipalPaid !== cost) {
        contentData.push([
          autoTextColor(
            allPrincipalYears.length <= 0
              ? "Warning: The principal paid details are not provided."
              : "Warning: The total principal paid does not match the asset cost."
          ),
        ]);
      }

      const assetCode =
        assetCodeList?.find((item) => item.assetId === asset.assetId)
          ?.assetCode || null;

      if (!pdf._meta) {
        pdf._meta = {
          footers: new Set(),
          curRef: null,
        };
      }
      pdf._meta.curRef = assetCode + " | " + asset.assetName;

      buildAutoTable({
        pdf,
        head: [
          [
            {
              content: "Asset Name: ",
              styles: { halign: "left" },
            },
            {
              content: asset.assetName || "HP Asset " + idx,
              styles: { halign: "left" },
            },
            asset.isCurrentYearDisposed
              ? {
                  content: "[Disposed]",
                  styles: {
                    fontStyle: "bold",
                    halign: "left",
                    fontSize: pdf.fs,
                  },
                }
              : "",
            "",
            "",
            "",
            "",
            "",
          ],
        ],
        body: contentData,
        checkHeaderCondition: true,
        customStartY: 55,
        schedule,
        company,
        columnStyles: {
          0: { halign: "left", cellWidth: 25 },
          1: { halign: "left", cellWidth: 45 },
          2: { halign: "left", cellWidth: 45 },
          3: { halign: "left", cellWidth: 20 },
          4: { halign: "left", cellWidth: 15 },
          5: { halign: "left", cellWidth: 20 },
          6: { halign: "left", cellWidth: 35 },
          7: { halign: "left", cellWidth: 20 },
        },
        printFooter: true,
        lineSettings: {},
      });

      // --- Principal Paid Table ---
      const principalPaidMap = {};
      for (const { year, amount } of principalPaid || []) {
        principalPaidMap[year] = amount;
      }

      const principalPaidRows = isS19Asset
        ? allPrincipalYears.map((year) => ({
            year,
            amount: principalPaidMap[year] || 0,
          }))
        : principalPaid;

      const yearTotals = {};
      const twdvTotals = {};
      const finalTotals = {};
      const totalAaClaims = calculateTotalAaClaims(principalDetails);
      let lastTotal = 0;
      allPrincipalYears.forEach((year) => {
        const entries = principalDetails[year] || [];
        const total = calculatePrincipalYearTotal(entries, isS19Asset);
        yearTotals[year] = total;
        const cur_pp = principalPaidMap[year] || 0;
        let netTotal = lastTotal + cur_pp - total;
        twdvTotals[year] = lastTotal;
        finalTotals[year] = netTotal;
        lastTotal = netTotal;
      });

      // Table header
      // --- Principal Paid Table (Chunked by Year Columns) ---
      const chunkSize = 15;
      const yearChunks = [];
      for (let i = 0; i < allPrincipalYears.length; i += chunkSize) {
        yearChunks.push(allPrincipalYears.slice(i, i + chunkSize));
      }

      yearChunks.forEach((yearChunk, chunkIdx) => {
        const head = [
          [
            "YA",
            yearChunk.length >= 15 ? "PP" : "Principal Paid",
            ...(isS19Asset ? ["AA Claims"] : []),
            ...yearChunk.map((year) => year.toString()),
          ],
        ];

        const body = principalPaidRows.map((row) => [
          `${row.year}`,
          formatAmount(row.amount),
          ...(isS19Asset
            ? [
                (() => {
                  const aaClaim = getAaClaimForPrincipalYear(
                    principalDetails,
                    row.year,
                    {
                      isCurrentYearDisposed: asset.isCurrentYearDisposed,
                      yearOfAssessment: company.yearOfAssessment,
                    }
                  );

                  return typeof aaClaim === "string"
                    ? aaClaim
                    : formatAmount(aaClaim, !shouldShowDecimals);
                })(),
              ]
            : []),
          ...yearChunk.map((colYear) => {
            const details = principalDetails?.[colYear] || [];
            const match = details.find((entry) => entry.from === row.year);
            const isDeferredRecord =
              match?.deferred ||
              (Array.isArray(asset.deferredYears) &&
                asset.deferredYears.includes(colYear));

            let displayAmount = "0";
            if (isDeferredRecord) displayAmount = "Deferred";
            else if (
              match?.disposed ||
              (asset.isCurrentYearDisposed &&
                colYear === company.yearOfAssessment)
            ) {
              displayAmount = "Disposed";

              if (colYear > company.yearOfAssessment) displayAmount = 0;
            } else if (match) {
              displayAmount = formatAmount(match.amount, !shouldShowDecimals);
            }

            return displayAmount;
          }),
        ]);

        if (isS19Asset) {
          body.push([
            boldAutoColumn("Initial Allowance", true),
            "",
            "",
            ...yearChunk.map((year) => {
              if (
                asset.isCurrentYearDisposed &&
                year >= company.yearOfAssessment
              ) {
                return {
                  content: "Forfeit",
                  styles: { fontStyle: "bold" },
                };
              }

              const iaForYear = (principalDetails?.[year] || []).reduce(
                (sum, entry) => sum + (entry.ia || 0),
                0
              );

              return {
                content: formatAmount(iaForYear, !shouldShowDecimals),
                styles: { fontStyle: "bold" },
              };
            }),
          ]);
        }

        // Subtotal and summary rows (limited to this chunk)
        body.push([
          { content: "Sub Total", styles: { fontStyle: "bold" } },
          {
            content: formatAmount(totalPrincipalPaid),
            styles: { fontStyle: "bold" },
          },
          ...(isS19Asset
            ? [
                {
                  content: formatAmount(totalAaClaims, !shouldShowDecimals),
                  styles: { fontStyle: "bold" },
                },
              ]
            : []),
          ...yearChunk.map((year) => ({
            content:
              asset.isCurrentYearDisposed && year > company.yearOfAssessment
                ? "Disposed"
                : formatAmount(
                    yearTotals[year] || 0,
                    !shouldShowDecimals,
                    true
                  ),
            styles: { fontStyle: "bold" },
          })),
        ]);

        body.push([
          { content: "TWDV bf", colSpan: 2, styles: { fontStyle: "bold" } },
          ...(isS19Asset ? [""] : []),
          ...yearChunk.map((year) => ({
            content:
              asset.isCurrentYearDisposed && year > company.yearOfAssessment
                ? "Disposed"
                : formatAmount(twdvTotals[year] || 0, !shouldShowDecimals),
            styles: { fontStyle: "bold" },
          })),
        ]);

        body.push([
          {
            content: "Principal paid",
            colSpan: 2,
            styles: { fontStyle: "bold" },
          },
          ...(isS19Asset ? [""] : []),
          ...yearChunk.map((year) => ({
            content:
              asset.isCurrentYearDisposed && year > company.yearOfAssessment
                ? "Disposed"
                : formatAmount(
                    principalPaidMap[year] || 0,
                    !shouldShowDecimals
                  ),
            styles: { fontStyle: "bold" },
          })),
        ]);

        body.push([
          boldAutoColumn("TWDV c/f"),
          "",
          ...(isS19Asset ? [""] : []),
          ...yearChunk.map((year) => ({
            content:
              asset.isCurrentYearDisposed && year > company.yearOfAssessment
                ? "Disposed"
                : formatAmount(finalTotals[year] || 0, !shouldShowDecimals),
            styles: { fontStyle: "bold" },
          })),
        ]);

        // Add notes only after last chunk
        if (chunkIdx === yearChunks.length - 1) {
          body.push([
            italicAutoColumn(
              "Notes: Sub Total = (a); TWDV b/f = (b); Principal paid = (c); TWDV c/f = (a) + (b) + (c)"
            ),
          ]);
        }

        buildAutoTable({
          pdf,
          head,
          body,
          checkHeaderCondition: true,
          customStartY:
            chunkIdx === 0
              ? pdf.startY - 8
              : isExcel
                ? pdf.startY + 8
                : pdf.doc.lastAutoTable.finalY + 8,
          schedule,
          company,
          headStyles: {
            fontStyle: "bold",
            halign: "center",
            fillColor: [240, 240, 240],
          },
          columnStyles: {
            0: { fontStyle: "bold", halign: "left", cellWidth: 20 },
            1: { fontStyle: "normal", halign: "center", minCellWidth: 10 },
          },
          didParseCell: function (data) {
            const colIndex = data.column.index;
            data.cell.styles.halign = colIndex >= 1 ? "center" : "left";
          },
          printFooter: true,
          lineSettings: {},
        });

        if (!isExcel) pdf.startY = pdf.doc.lastAutoTable.finalY + 5;
      });

      // After each asset except the last, add a new page and re-add the company header
      if (idx < assets.length - 1 && !isExcel) {
        pdf.doc.addPage();
        pdf.startY = pdf.initY;
      }
    });
  }

  if (
    summaryData &&
    caTypeTotals &&
    Object.keys(summaryData || {}).length !== 0
  ) {
    if (!isExcel) pdf.doc.addPage();
    pdf._meta.curRef = "Hire Purchase Summary";
    pdf.startY = pdf.initY;
    const caTypes = Object.keys(summaryData);
    const nonS19Types = caTypes.filter((caType) => caType !== "S19");
    const s19Types = caTypes.filter((caType) => caType === "S19");

    const buildSummaryBody = (types, isS19 = false) => {
      let body = [];
      types.forEach((caType) => {
        const caTypeTotal = caTypeTotals[caType];
        let assetIndex = 0;
        let totalPp = 0;
        let totalIa = 0;
        let totalAa = 0;

        for (const key in summaryData[caType]) {
          const entry = summaryData[caType][key];
          if (isS19) {
            body.push([
              assetIndex === 0 ? caType : "",
              `${entry.year}`,
              formatAmount(entry.principalPaid),
              entry.iaDisplay === "Forfeit" || entry.disposed
                ? "Forfeit"
                : formatAmount(entry.ia || 0),
              formatAmount(entry.aa || 0),
              `${entry.noOfYears}`,
              `${entry.noOfYearsBf}`,
              entry.deferred ? "Yes" : "No",
            ]);
            totalIa += entry.ia || 0;
            totalAa += entry.aa || 0;
          } else {
            body.push([
              assetIndex === 0 ? caType : "",
              `${entry.year}`,
              formatAmount(entry.principalPaid),
              `${entry.noOfYears}`,
              `${entry.noOfYearsBf}`,
              entry.deferred ? "Yes" : "No",
              formatAmount(entry.twdvBf),
            ]);
          }

          totalPp += entry.principalPaid;
          assetIndex++;
        }

        if (isS19) {
          body.push([
            {
              content: `${caType} Total`,
              styles: { fontStyle: "bold" },
            },
            "",
            applyTopBorder(formatAmount(totalPp), true),
            applyTopBorder(formatAmount(totalIa), true),
            applyTopBorder(formatAmount(totalAa), true),
            "",
            "",
            "",
          ]);
        } else {
          body.push([
            {
              content: `${caType} Total`,
              styles: { fontStyle: "bold" },
            },
            "",
            applyTopBorder(formatAmount(totalPp), true),
            "",
            "",
            "",
            applyTopBorder(formatAmount(caTypeTotal), true),
          ]);
        }
        body.push([]);
      });

      return body;
    };

    let haveNonS19Types = false;
    if (nonS19Types.length > 0) {
      haveNonS19Types = true;
      buildAutoTable({
        pdf,
        checkHeaderCondition: true,
        addPlainTitle: true,
        tableTitle: "Hire Purchase Summary",
        head: [
          [
            "CA Type",
            "Principal Year",
            "Principal Paid",
            "No of Years",
            "No of Years b/f",
            `Deferred in YA ${company.yearOfAssessment}`,
            "TWDV b/f",
          ],
        ],
        body: buildSummaryBody(nonS19Types, false),
        schedule,
        company,
        headStyles: {
          fontStyle: "bold",
          halign: "center",
          fontSize: pdf.fs,
          fillColor: [240, 240, 240],
        },
        columnStyles: {
          0: { halign: "left", cellWidth: 60 },
          1: { halign: "center" },
          2: { halign: "center" },
          3: { halign: "center" },
          4: { halign: "center" },
          5: { halign: "center" },
          6: { halign: "right", cellWidth: 25 },
        },
        printFooter: true,
        lineSettings: {},
      });
    }

    if (s19Types.length > 0) {
      buildAutoTable({
        pdf,
        customStartY:
          !haveNonS19Types || isExcel ? 65 : pdf.doc.lastAutoTable.finalY,
        checkHeaderCondition: true,
        addPlainTitle: !haveNonS19Types,
        tableTitle: haveNonS19Types ? "" : "Hire Purchase Summary",
        head: [
          [
            "CA Type",
            "Principal Year",
            "Principal Paid",
            "IA",
            "AA",
            "No of Years",
            "No of Years b/f",
            `Deferred in YA ${company.yearOfAssessment}`,
          ],
        ],
        body: buildSummaryBody(s19Types, true),
        schedule,
        company,
        headStyles: {
          fontStyle: "bold",
          halign: "center",
          fontSize: pdf.fs,
          fillColor: [240, 240, 240],
        },
        columnStyles: {
          0: { halign: "left", cellWidth: 50 },
          1: { halign: "center" },
          2: { halign: "center" },
          3: { halign: "center" },
          4: { halign: "center" },
          5: { halign: "center" },
          6: { halign: "center" },
          7: { halign: "center" },
        },
        printFooter: true,
        lineSettings: {},
      });
    }
  }

  pdf._meta.curRef = "Notes";
  buildAutoNote(pdf, forView, company, schedule, notes, reviewNotes, [], true);
  pdf.startY = pdf.initY;

  insertTableOfContents(pdf, company, schedule);

  return pdf.getDoc(forView, company, schedule);
};
